import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
  MouseEvent,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormHelperText,
  Link,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
import { VBMCardTitle, VBMCardWrapper } from '..';
import useResponsive from '@/hooks/useResponsive';
import { Iconify } from '@/components';
import Label from '@/components/Label';
import { useSelector } from '@/stores';
import { fCurrency } from '@/utils/formatNumber';
import { fDateTime } from '@/utils/formatTime';
import {
  BookingSession,
  Wallet as BookingWallet,
} from '@/features/bookings-management/types';
import {
  resolveBookingSessionBillableHours,
  retrieveExtrasFromId,
  transformExtraIds,
} from '@/features/bookings-management/utils';
import { Wallet as UserWallet } from '@/features/users/shared/types';
import {
  applyCouponToBooking,
  sendBookingPaymentMethodRequest,
  updateBookingPaymentMethod,
} from '@/features/bookings-management/api';
import {
  chargeBookingSessionBalance,
  chargeBookingSessionCustomAmount,
  holdBookingSessionOutstandingBalance,
  recordBookingSessionPayment,
  RecordBookingSessionPaymentPayload,
  RecordBookingSessionPaymentResponse,
  repriceBookingSession,
  refundBookingSessionPayment,
  RepriceBookingSessionResponse,
  sendBookingSessionPaymentLink,
  updateBookingSession,
  voidBookingSessionPayment,
  VoidBookingSessionPaymentPayload,
  VoidBookingSessionPaymentResponse,
} from '@/features/bookings-management/api/sessions';
import { getCoupons, verifyCoupon } from '@/features/coupons/api';
import { getSingleUser } from '@/features/users/shared/api';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useAuth from '@/hooks/useAuth';
import { deleteTransaction } from '@/features/transactions/api';
import {
  formatTransactionAmount,
  formatTransactionSource,
  getTransactionStatusPresentation,
  getTransactionTypeLabel,
  isRefundLikeTransaction,
  isVoidLikeTransaction,
} from '@/features/transactions/utils';

type AnyWallet = BookingWallet | UserWallet;
type SessionTransaction = BookingSession['payment_transaction'][number];

type WalletDisplay = {
  primary: string;
  secondary?: string;
  tooltipLines: string[];
  isDefault: boolean;
};

type RepricePreviewRow = {
  sessionId: string;
  date: string;
  currentHourlyRate: number | null;
  newHourlyRate: number | null;
  currentTotal: number | null;
  newTotal: number | null;
  delta: number | null;
  skippedReason?: string;
};

const getExpiryLabel = (wallet: AnyWallet) => {
  if (!wallet.exp_month || !wallet.exp_year) return '';
  const month = wallet.exp_month.toString().padStart(2, '0');
  const year = wallet.exp_year.toString().slice(-2);
  return `Expires ${month}/${year}`;
};

const buildWalletDisplay = (
  wallet?: AnyWallet | null
): WalletDisplay | null => {
  if (!wallet) return null;

  const descriptor = wallet.brand?.trim() || wallet.type?.trim();
  const lastFour = wallet.last4 ? `•••• ${wallet.last4}` : '';
  const primary = [`(${descriptor || 'Payment method'})`, lastFour]
    .filter(Boolean)
    .join(' ')
    .trim();

  const expiryLabel = getExpiryLabel(wallet);
  const lastUsedLabel = wallet.last_used
    ? `Last used ${fDateTime(wallet.last_used)}`
    : '';

  const secondary = [expiryLabel, lastUsedLabel].filter(Boolean).join(' • ');

  const tooltipLines = [
    wallet.default ? 'Default payment method' : '',
    expiryLabel,
    lastUsedLabel,
  ].filter(Boolean);

  return {
    primary,
    secondary: secondary || undefined,
    tooltipLines,
    isDefault: Boolean(wallet.default),
  };
};

const WalletSummary = ({ wallet }: { wallet?: AnyWallet | null }) => {
  const display = buildWalletDisplay(wallet);
  if (!display) return null;

  const tooltipTitle = display.tooltipLines.length ? (
    <Stack spacing={0.25}>
      {display.tooltipLines.map((line) => (
        <Typography key={line} variant="caption">
          {line}
        </Typography>
      ))}
    </Stack>
  ) : null;

  const content = (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ textTransform: 'none' }}
      >
        {display.primary}
      </Typography>
      {display.isDefault && (
        <Typography
          variant="caption"
          color="success.main"
          sx={{ fontWeight: 600, textTransform: 'uppercase' }}
        >
          Default
        </Typography>
      )}
      {tooltipTitle && (
        <Tooltip title={tooltipTitle} placement="top">
          <Box sx={{ display: 'flex', color: 'text.secondary' }}>
            <Iconify icon="eva:info-outline" width={16} />
          </Box>
        </Tooltip>
      )}
    </Stack>
  );

  return content;
};

const findWalletByIdentifiers = (
  wallets: BookingWallet[],
  identifiers: { id?: number | null; stripeId?: string | null }
) => {
  if (!wallets.length) return null;
  const { id, stripeId } = identifiers;

  if (typeof id === 'number') {
    const matchById = wallets.find((wallet) => wallet.id === id);
    if (matchById) return matchById;
  }

  if (stripeId) {
    const matchByStripe = wallets.find(
      (wallet) => wallet.stripe_id === stripeId
    );
    if (matchByStripe) return matchByStripe;

    const numericStripeId = Number(stripeId);
    if (!Number.isNaN(numericStripeId)) {
      const matchByNumeric = wallets.find(
        (wallet) => wallet.id === numericStripeId
      );
      if (matchByNumeric) return matchByNumeric;
    }
  }

  return null;
};

const getWalletStripeIdentity = (wallet: BookingWallet) => {
  const stripeId = wallet.stripe_id?.trim().toLowerCase();
  if (stripeId) return `stripe:${stripeId}`;

  return '';
};

const getWalletDisplayIdentity = (wallet: BookingWallet) => {
  const descriptor = [wallet.name, wallet.brand, wallet.type]
    .map((value) => value?.trim().toLowerCase())
    .filter(Boolean)
    .join('|');
  const expiry = [wallet.exp_month, wallet.exp_year]
    .map((value) => value?.toString().trim())
    .filter(Boolean)
    .join('/');
  const last4 = wallet.last4?.trim();

  if (!descriptor && !last4 && !expiry) return '';

  return [
    'display',
    wallet.user_id || '',
    descriptor,
    last4 || '',
    expiry,
  ].join(':');
};

const getLatestDateValue = (
  first?: string | number | Date | null,
  second?: string | number | Date | null
) => {
  if (!first) return second;
  if (!second) return first;

  const firstTimestamp = new Date(first).getTime();
  const secondTimestamp = new Date(second).getTime();

  if (!Number.isFinite(firstTimestamp)) return second;
  if (!Number.isFinite(secondTimestamp)) return first;

  return secondTimestamp > firstTimestamp ? second : first;
};

const mergeWalletDetails = (
  existing: BookingWallet,
  incoming: BookingWallet
): BookingWallet => ({
  ...existing,
  stripe_id: existing.stripe_id || incoming.stripe_id,
  user_id: existing.user_id || incoming.user_id,
  name: existing.name || incoming.name,
  brand: existing.brand || incoming.brand,
  country: existing.country || incoming.country,
  exp_month: existing.exp_month || incoming.exp_month,
  exp_year: existing.exp_year || incoming.exp_year,
  last4: existing.last4 || incoming.last4,
  type: existing.type || incoming.type,
  default: Boolean(existing.default || incoming.default),
  last_used: getLatestDateValue(existing.last_used, incoming.last_used),
  created_at: existing.created_at || incoming.created_at,
  updated_at: getLatestDateValue(
    existing.updated_at,
    incoming.updated_at
  ) as string,
});

const maskPaymentMethodId = (value?: string | null) => {
  if (!value) return '';
  if (value.startsWith('pm_') && value.length > 10) {
    return `${value.slice(0, 6)}•••${value.slice(-4)}`;
  }
  return value;
};

const toCurrency = (value: number) => fCurrency(String(Math.max(value, 0)));

const buildStripeDashboardPaymentUrl = (paymentIntentId: string) => {
  const rawBaseUrl = process.env.REACT_APP_STRIPE_DASHBOARD_URL?.trim();
  const baseUrl =
    rawBaseUrl?.replace(/\/+$/, '') || 'https://dashboard.stripe.com';
  const configuredMode = process.env.REACT_APP_STRIPE_DASHBOARD_MODE?.trim();
  const isLiveMode =
    configuredMode === 'live' ||
    (configuredMode !== 'test' && process.env.NODE_ENV === 'production');

  const pathPrefix = isLiveMode ? '' : 'test/';

  return `${baseUrl}/${pathPrefix}payments/${paymentIntentId}`;
};

const toSignedCurrency = (value: number) => {
  const formatted = fCurrency(Math.abs(value));
  return value < 0 ? `-${formatted}` : formatted;
};

const toDeltaCurrency = (value: number) => {
  const formatted = fCurrency(Math.abs(value));
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return fCurrency(0);
};

const getRecordValue = (
  record: Record<string, unknown>,
  keys: string[]
): unknown => {
  const matchedKey = keys.find((key) => key in record);
  return matchedKey ? record[matchedKey] : undefined;
};

const getRecordString = (
  record: Record<string, unknown>,
  keys: string[]
): string => {
  const value = getRecordValue(record, keys);

  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);

  return '';
};

const getRecordNumber = (
  record: Record<string, unknown>,
  keys: string[]
): number | null => {
  const value = getRecordValue(record, keys);
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return null;
  }

  const normalizedValue = Number(value);

  return Number.isFinite(normalizedValue) ? normalizedValue : null;
};

const getFirstRecordNumber = (
  records: Array<Record<string, unknown> | null>,
  keys: string[]
): number | null => {
  for (const record of records) {
    if (!record) continue;

    const value = getRecordNumber(record, keys);
    if (value !== null) return value;
  }

  return null;
};

const getNestedRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
};

const getNestedRecordFromKeys = (
  record: Record<string, unknown> | null,
  keys: string[]
) => {
  if (!record) return null;

  return getNestedRecord(getRecordValue(record, keys));
};

const getRecordArray = (
  source: Record<string, unknown> | null,
  keys: string[]
): Record<string, unknown>[] => {
  if (!source) return [];

  const matchedKey = keys.find((key) => Array.isArray(source[key]));
  if (!matchedKey) return [];

  return (source[matchedKey] as unknown[]).filter(
    (item): item is Record<string, unknown> =>
      Boolean(item) && typeof item === 'object' && !Array.isArray(item)
  );
};

const getNestedRecords = (
  source: Record<string, unknown> | null,
  keys: string[]
): Record<string, unknown>[] => {
  if (!source) return [];

  const visited = new Set<unknown>();
  const queue: Array<Record<string, unknown>> = [source];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || visited.has(current)) continue;
    visited.add(current);

    const directMatches = getRecordArray(current, keys);
    if (directMatches.length > 0) {
      return directMatches;
    }

    Object.values(current).forEach((value) => {
      const nestedRecord = getNestedRecord(value);
      if (nestedRecord && !visited.has(nestedRecord)) {
        queue.push(nestedRecord);
      }
    });
  }

  return [];
};

const getSessionHourlyRate = (
  session: BookingSession | Record<string, unknown> | null | undefined
) => {
  const sessionRecord = getNestedRecord(session);
  const bookingDetailsRecord = getNestedRecord(sessionRecord?.booking_details);
  const billingRecord = getNestedRecord(sessionRecord?.billing);
  const manualHourlyRate = getFirstRecordNumber(
    [bookingDetailsRecord, sessionRecord],
    ['manual_price_per_hour', 'manualPricePerHour']
  );

  return (
    manualHourlyRate ??
    getFirstRecordNumber(
      [sessionRecord, billingRecord],
      ['price_per_hour', 'pricePerHour']
    )
  );
};

const getSessionTotalAmount = (
  session: BookingSession | Record<string, unknown> | null | undefined
) => {
  const sessionRecord = getNestedRecord(session);
  const billingRecord = getNestedRecord(sessionRecord?.billing);

  return (
    getFirstRecordNumber(
      [billingRecord, sessionRecord],
      ['total_amount_after_discount', 'totalAmountAfterDiscount']
    ) ??
    getFirstRecordNumber(
      [billingRecord, sessionRecord],
      ['total_amount', 'totalAmount']
    )
  );
};

const getRepricePreviewSessionRecord = (item: Record<string, unknown>) =>
  getNestedRecordFromKeys(item, [
    'booking_session',
    'bookingSession',
    'session',
    'updated_session',
    'updatedSession',
  ]);

const getRepricePreviewBillableHours = (
  item: Record<string, unknown>,
  sessionRecord: Record<string, unknown> | null
) => {
  const billingRecord = getNestedRecord(sessionRecord?.billing);
  const explicitHours = getFirstRecordNumber(
    [item, sessionRecord, billingRecord],
    [
      'billable_hours',
      'billableHours',
      'billing_total_hours',
      'billingTotalHours',
    ]
  );
  if (explicitHours !== null) return explicitHours;

  const sessionHours = resolveBookingSessionBillableHours(
    sessionRecord as Partial<BookingSession> | null
  );
  if (sessionHours > 0) return sessionHours;

  const itemHours = resolveBookingSessionBillableHours(
    item as Partial<BookingSession>
  );

  return itemHours > 0 ? itemHours : null;
};

const getCalculatedRepriceTotal = (
  hourlyRate: number | null,
  billableHours: number | null
) => {
  if (hourlyRate === null || billableHours === null) return null;

  return hourlyRate * billableHours;
};

const getCalculatedRepriceNewTotal = ({
  currentHourlyRate,
  newHourlyRate,
  currentTotal,
  billableHours,
}: {
  currentHourlyRate: number | null;
  newHourlyRate: number | null;
  currentTotal: number | null;
  billableHours: number | null;
}) => {
  if (
    currentHourlyRate === null ||
    newHourlyRate === null ||
    currentTotal === null ||
    billableHours === null
  ) {
    return null;
  }

  return currentTotal + (newHourlyRate - currentHourlyRate) * billableHours;
};

const REPRICE_PREVIEW_ELIGIBLE_STATUSES = new Set([
  'preview',
  'eligible',
  'updated',
  'applied',
  'repriced',
  'success',
]);

const REPRICE_PREVIEW_SKIPPED_STATUSES = new Set([
  'skipped',
  'skip',
  'blocked',
  'ineligible',
  'not_eligible',
  'failed',
  'error',
]);

const getRepricePreviewStatus = (item: Record<string, unknown>) =>
  getRecordString(item, ['status', 'state']).toLowerCase();

const getRepriceSkipReason = (item: Record<string, unknown>) =>
  getRecordString(item, [
    'skipped_reason',
    'skip_reason',
    'skippedReason',
    'skipReason',
    'reason',
    'message',
  ]);

const isSkippedRepricePreviewItem = (item: Record<string, unknown>) => {
  const status = getRepricePreviewStatus(item);

  if (REPRICE_PREVIEW_SKIPPED_STATUSES.has(status)) return true;
  if (REPRICE_PREVIEW_ELIGIBLE_STATUSES.has(status)) return false;

  return Boolean(getRepriceSkipReason(item));
};

const normalizeRepricePreviewRow = (
  item: Record<string, unknown>,
  requestedHourlyRate: number
): RepricePreviewRow => {
  const sessionRecord = getRepricePreviewSessionRecord(item);
  const currentHourlyRate =
    getRecordNumber(item, [
      'current_price_per_hour',
      'old_price_per_hour',
      'current_hourly_rate',
      'currentHourlyRate',
      'oldPricePerHour',
      'currentPricePerHour',
    ]) ??
    getSessionHourlyRate(sessionRecord) ??
    getSessionHourlyRate(item);
  const newHourlyRate =
    getRecordNumber(item, [
      'new_price_per_hour',
      'new_hourly_rate',
      'requested_price_per_hour',
      'newHourlyRate',
      'newPricePerHour',
      'requestedPricePerHour',
    ]) ?? requestedHourlyRate;
  const billableHours = getRepricePreviewBillableHours(item, sessionRecord);
  const currentTotal =
    getRecordNumber(item, [
      'current_total',
      'old_total',
      'current_total_amount',
      'current_total_amount_after_discount',
      'currentTotal',
      'oldTotal',
      'currentTotalAmount',
      'currentTotalAmountAfterDiscount',
    ]) ??
    getSessionTotalAmount(sessionRecord) ??
    getSessionTotalAmount(item) ??
    getCalculatedRepriceTotal(currentHourlyRate, billableHours);
  const newTotal =
    getRecordNumber(item, [
      'new_total',
      'new_total_amount',
      'new_total_amount_after_discount',
      'repriced_total',
      'updated_total',
      'newTotal',
      'newTotalAmount',
      'newTotalAmountAfterDiscount',
      'repricedTotal',
      'updatedTotal',
    ]) ??
    getCalculatedRepriceNewTotal({
      currentHourlyRate,
      newHourlyRate,
      currentTotal,
      billableHours,
    });
  const delta =
    getRecordNumber(item, [
      'delta',
      'difference',
      'amount_difference',
      'delta_amount_after_discount',
      'amountDifference',
      'deltaAmountAfterDiscount',
    ]) ??
    (currentTotal !== null && newTotal !== null
      ? newTotal - currentTotal
      : null);
  const sessionId =
    getRecordString(item, ['session_id', 'sessionId', 'id']) ||
    'Unknown session';
  const date =
    getRecordString(item, [
      'date',
      'session_date',
      'booking_date',
      'sessionDate',
      'bookingDate',
    ]) || '—';
  const skippedReason = getRepriceSkipReason(item);

  return {
    sessionId,
    date,
    currentHourlyRate,
    newHourlyRate,
    currentTotal,
    newTotal,
    delta,
    skippedReason: skippedReason || undefined,
  };
};

const normalizeRepricePreviewResponse = (
  response: RepriceBookingSessionResponse,
  requestedHourlyRate: number
) => {
  const rootRecord = getNestedRecord(response);
  const resultItems = getNestedRecords(rootRecord, ['results']);

  if (resultItems.length > 0) {
    const skippedItems = resultItems.filter(isSkippedRepricePreviewItem);
    const eligibleItems = resultItems.filter(
      (item) => !isSkippedRepricePreviewItem(item)
    );

    return {
      eligible: eligibleItems.map((item) =>
        normalizeRepricePreviewRow(item, requestedHourlyRate)
      ),
      skipped: skippedItems.map((item) =>
        normalizeRepricePreviewRow(item, requestedHourlyRate)
      ),
    };
  }

  const eligibleItems = getNestedRecords(rootRecord, [
    'eligible',
    'eligible_sessions',
    'eligibleSessions',
    'sessions_to_update',
    'sessionsToUpdate',
  ]);
  const skippedItems = getNestedRecords(rootRecord, [
    'skipped',
    'skipped_sessions',
    'skippedSessions',
    'blocked',
    'blocked_sessions',
    'blockedSessions',
  ]);

  return {
    eligible: eligibleItems.map((item) =>
      normalizeRepricePreviewRow(item, requestedHourlyRate)
    ),
    skipped: skippedItems.map((item) =>
      normalizeRepricePreviewRow(item, requestedHourlyRate)
    ),
  };
};

const uniqueBookingSessions = (sessions: BookingSession[]) => {
  const seenSessionIds = new Set<number>();

  return sessions.filter((session) => {
    if (!session?.id || seenSessionIds.has(session.id)) return false;

    seenSessionIds.add(session.id);
    return true;
  });
};

const getBookingSessionArray = (
  record: Record<string, unknown> | null,
  keys: string[]
): BookingSession[] =>
  getRecordArray(record, keys) as unknown as BookingSession[];

const getBookingSessionFromResponse = (
  record: Record<string, unknown> | null
): BookingSession[] => {
  const sessionRecord = getNestedRecordFromKeys(record, [
    'booking_session',
    'bookingSession',
    'session',
    'updated_session',
    'updatedSession',
  ]);

  return sessionRecord ? [sessionRecord as unknown as BookingSession] : [];
};

const getRepricedSessions = (response: RepriceBookingSessionResponse) => {
  const rootRecord = getNestedRecord(response);
  const nestedDataRecord = getNestedRecord(rootRecord?.data);
  const responseSessionKeys = [
    'updated_sessions',
    'updatedSessions',
    'booking_sessions',
    'bookingSessions',
    'sessions',
  ];

  return uniqueBookingSessions([
    ...getBookingSessionArray(rootRecord, responseSessionKeys),
    ...getBookingSessionArray(nestedDataRecord, responseSessionKeys),
    ...getBookingSessionFromResponse(rootRecord),
    ...getBookingSessionFromResponse(nestedDataRecord),
  ]);
};

const getRepricedSessionsCount = (response: RepriceBookingSessionResponse) => {
  const repricedSessions = getRepricedSessions(response);
  if (repricedSessions.length > 0) return repricedSessions.length;

  const rootRecord = getNestedRecord(response);
  const nestedDataRecord = getNestedRecord(rootRecord?.data);
  const updatedSessions = rootRecord?.updated_sessions;
  const sessions = rootRecord?.sessions;
  const nestedUpdatedSessions = nestedDataRecord?.updated_sessions;
  const nestedSessions = nestedDataRecord?.sessions;

  if (Array.isArray(updatedSessions)) {
    return updatedSessions.length;
  }

  if (Array.isArray(sessions)) {
    return sessions.length;
  }

  if (Array.isArray(nestedUpdatedSessions)) {
    return nestedUpdatedSessions.length;
  }

  if (Array.isArray(nestedSessions)) {
    return nestedSessions.length;
  }

  return 0;
};

const renderCurrencyOrDash = (value: number | null) =>
  value === null ? '—' : toCurrency(value);

const REFUND_REASON_OPTIONS = [
  { value: 'requested_by_customer', label: 'Requested by customer' },
  { value: 'duplicate', label: 'Duplicate payment' },
  { value: 'service_issue', label: 'Service issue' },
  { value: 'fraudulent', label: 'Fraudulent' },
  { value: 'other', label: 'Other' },
] as const;

const VOID_PAYMENT_TYPE_OPTIONS = [
  { value: 'full', label: 'Full void' },
  { value: 'partial', label: 'Partial void' },
] as const;

type VoidPaymentType = (typeof VOID_PAYMENT_TYPE_OPTIONS)[number]['value'];

const isRefundTransaction = (transaction: SessionTransaction) =>
  isRefundLikeTransaction(transaction);

const isVoidTransaction = (transaction: SessionTransaction) =>
  isVoidLikeTransaction(transaction);

const NUMERIC_TRANSACTION_STATUS_KEYS: Record<number, string> = {
  0: 'pending',
  1: 'succeeded',
  2: 'onhold',
  3: 'failed',
  4: 'processing',
  5: 'cancelled',
};

const UNSETTLED_TRANSACTION_STATUS_KEYS = new Set([
  'pending',
  'processing',
  'created',
  'requires_action',
  'requires_payment_method',
  'onhold',
  'cancelled',
  'canceled',
]);

const SETTLED_TRANSACTION_STATUS_KEYS = new Set([
  'succeeded',
  'success',
  'paid',
  'partially_refunded',
  'refunded',
  'reversed',
  'void',
  'voided',
]);

const getRawTransactionStatusKey = (status: unknown) => {
  if (status === null || typeof status === 'undefined') return '';

  const normalized = status.toString().trim().toLowerCase();
  if (/^-?\d+$/.test(normalized)) {
    return NUMERIC_TRANSACTION_STATUS_KEYS[Number(normalized)] ?? normalized;
  }

  return normalized;
};

const getTransactionStatusKey = (transaction: SessionTransaction) =>
  getTransactionStatusPresentation(transaction)
    .label.toLowerCase()
    .replace(/\s+/g, '_');

const isSettledTransaction = (transaction: SessionTransaction) => {
  const rawStatusKey = getRawTransactionStatusKey(transaction.status);
  if (UNSETTLED_TRANSACTION_STATUS_KEYS.has(rawStatusKey)) {
    return false;
  }
  if (rawStatusKey === 'failed') {
    return isRefundTransaction(transaction);
  }

  const statusKey = getTransactionStatusKey(transaction);
  return SETTLED_TRANSACTION_STATUS_KEYS.has(statusKey);
};

const getTransactionMetadataValue = (
  transaction: SessionTransaction,
  key: string
) => {
  const { metadata } = transaction;
  if (!metadata || typeof metadata !== 'object') return null;

  const value = metadata[key];
  return typeof value === 'string' || typeof value === 'number' ? value : null;
};

const getTransactionMetadataBooleanValue = (
  transaction: SessionTransaction,
  key: string
) => {
  const { metadata } = transaction;
  if (!metadata || typeof metadata !== 'object') return false;

  const value = metadata[key];
  if (value === true) return true;
  if (typeof value === 'number') return value === 1;
  if (typeof value !== 'string') return false;

  return ['1', 'true', 'yes'].includes(value.trim().toLowerCase());
};

const TRANSACTION_METADATA_DETAIL_FIELDS = [
  { label: 'Flow', keys: ['flow', 'payment_flow', 'paymentFlow'] },
  {
    label: 'Stripe status',
    keys: ['stripe_status', 'stripeStatus'],
  },
  {
    label: 'Error',
    keys: ['error', 'stripe_error', 'stripeError'],
  },
  {
    label: 'Hold failure',
    keys: ['hold_failure', 'holdFailure', 'holdfailure'],
  },
  {
    label: 'Payment error',
    keys: [
      'last_payment_error',
      'lastPaymentError',
      'payment_error',
      'paymentError',
    ],
  },
  {
    label: 'Failure reason',
    keys: [
      'failure_reason',
      'failureReason',
      'decline_reason',
      'declineReason',
    ],
  },
  { label: 'Decline code', keys: ['decline_code', 'declineCode'] },
] as const;

const METADATA_OBJECT_FIELD_ORDER = [
  'message',
  'reason',
  'type',
  'code',
  'decline_code',
  'declineCode',
  'failed_at',
  'failedAt',
  'payment_intent_id',
  'paymentIntentId',
  'payment_intent',
  'paymentIntent',
  'stripe_status',
  'stripeStatus',
];

const isMetadataRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const hasMetadataDisplayValue = (value: unknown): boolean => {
  if (value === null || typeof value === 'undefined') return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.some(hasMetadataDisplayValue);
  if (isMetadataRecord(value)) {
    return Object.values(value).some(hasMetadataDisplayValue);
  }

  return false;
};

const formatMetadataLabel = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/^./, (char) => char.toUpperCase())
    .replace(/\bid\b/g, 'ID')
    .replace(/\burl\b/g, 'URL');

const isDateMetadataKey = (key?: string) => {
  if (!key) return false;

  const normalizedKey = key
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase();

  return (
    normalizedKey.endsWith('_at') ||
    normalizedKey.endsWith('_date') ||
    normalizedKey === 'date'
  );
};

const formatMetadataScalarValue = (value: unknown, key?: string) => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) return '';

    if (isDateMetadataKey(key)) {
      const timestamp = Date.parse(trimmedValue);
      if (Number.isFinite(timestamp)) return fDateTime(trimmedValue);
    }

    return trimmedValue;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value.toString() : '';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return '';
};

const formatMetadataInlineValue = (value: unknown, key?: string): string => {
  const scalarValue = formatMetadataScalarValue(value, key);
  if (scalarValue) return scalarValue;

  if (Array.isArray(value)) {
    return value
      .map((item) => formatMetadataInlineValue(item))
      .filter(Boolean)
      .join(', ');
  }

  if (isMetadataRecord(value)) {
    return Object.entries(value)
      .filter(([, nestedValue]) => hasMetadataDisplayValue(nestedValue))
      .map(
        ([nestedKey, nestedValue]) =>
          `${formatMetadataLabel(nestedKey)}: ${formatMetadataInlineValue(
            nestedValue,
            nestedKey
          )}`
      )
      .join('; ');
  }

  return '';
};

const getOrderedMetadataEntries = (value: Record<string, unknown>) => {
  const entries = Object.entries(value).filter(([, nestedValue]) =>
    hasMetadataDisplayValue(nestedValue)
  );

  const orderedEntries = METADATA_OBJECT_FIELD_ORDER.flatMap((orderedKey) =>
    entries.filter(([key]) => key === orderedKey)
  );
  const remainingEntries = entries.filter(
    ([key]) => !METADATA_OBJECT_FIELD_ORDER.includes(key)
  );

  return [...orderedEntries, ...remainingEntries];
};

const renderMetadataObjectValue = (value: Record<string, unknown>) => {
  const entries = getOrderedMetadataEntries(value);
  if (!entries.length) return null;

  return (
    <Stack spacing={0.25} alignItems="flex-end" sx={{ maxWidth: '100%' }}>
      {entries.map(([key, nestedValue]) => (
        <Typography
          key={key}
          variant="body2"
          sx={{ overflowWrap: 'anywhere', whiteSpace: 'normal' }}
        >
          {formatMetadataLabel(key)}:{' '}
          {formatMetadataInlineValue(nestedValue, key)}
        </Typography>
      ))}
    </Stack>
  );
};

const renderTransactionMetadataValue = (value: unknown): ReactNode | null => {
  if (!hasMetadataDisplayValue(value)) return null;
  if (isMetadataRecord(value)) return renderMetadataObjectValue(value);

  const inlineValue = formatMetadataInlineValue(value);
  return inlineValue || null;
};

const getTransactionMetadataRawValue = (
  transaction: SessionTransaction,
  keys: readonly string[]
) => {
  const { metadata } = transaction;
  if (!isMetadataRecord(metadata)) return null;

  return keys.map((key) => metadata[key]).find(hasMetadataDisplayValue) ?? null;
};

const getTransactionMetadataDetailRows = (
  transaction: SessionTransaction
): { label: string; value: ReactNode }[] =>
  TRANSACTION_METADATA_DETAIL_FIELDS.flatMap(({ label, keys }) => {
    const value = getTransactionMetadataRawValue(transaction, keys);
    const renderedValue = renderTransactionMetadataValue(value);

    return renderedValue ? [{ label, value: renderedValue }] : [];
  });

const getTransactionPaymentIntent = (transaction: SessionTransaction) =>
  transaction.payment_intent?.trim() || '';

const getRefundedTransactionId = (transaction: SessionTransaction) => {
  const directValue = transaction.refunded_transaction_id;
  if (typeof directValue === 'number' && Number.isFinite(directValue)) {
    return directValue;
  }

  const metadataValue = ['refunded_transaction_id', 'original_transaction_id']
    .map((key) => getTransactionMetadataValue(transaction, key))
    .find((value) => {
      const normalizedValue = Number(value);
      return Number.isFinite(normalizedValue) && normalizedValue > 0;
    });
  const normalizedValue = Number(metadataValue);

  return Number.isFinite(normalizedValue) ? normalizedValue : null;
};

const getTransactionReferenceId = (
  transaction: SessionTransaction,
  directKeys: (keyof SessionTransaction)[],
  metadataKeys: string[]
) => {
  const directValue = directKeys
    .map((key) => transaction[key])
    .find((value) => {
      const normalizedValue = Number(value);
      return Number.isFinite(normalizedValue) && normalizedValue > 0;
    });

  if (directValue) {
    return Number(directValue);
  }

  const metadataValue = metadataKeys
    .map((key) => getTransactionMetadataValue(transaction, key))
    .find((value) => {
      const normalizedValue = Number(value);
      return Number.isFinite(normalizedValue) && normalizedValue > 0;
    });

  return metadataValue ? Number(metadataValue) : null;
};

const getVoidedTransactionId = (transaction: SessionTransaction) =>
  getTransactionReferenceId(
    transaction,
    [
      'voided_transaction_id',
      'voided_payment_transaction_id',
      'reversed_transaction_id',
    ],
    [
      'voided_transaction_id',
      'voided_payment_transaction_id',
      'reversed_transaction_id',
      'original_transaction_id',
      'original_payment_transaction_id',
      'payment_transaction_id',
      'source_transaction_id',
      'transaction_id',
      'manual_payment_transaction_id',
    ]
  );

const getStripeRefundReference = (transaction: SessionTransaction) => {
  const directValue = transaction.stripe_refund_id?.trim();
  if (directValue) return directValue;

  const metadataValue = getTransactionMetadataValue(
    transaction,
    'stripe_refund_id'
  );
  return typeof metadataValue === 'string' ? metadataValue.trim() : '';
};

const getRefundReason = (transaction: SessionTransaction) => {
  const directValue = transaction.refund_reason?.trim();
  if (directValue) return directValue;

  const metadataValue = getTransactionMetadataValue(
    transaction,
    'refund_reason'
  );
  return typeof metadataValue === 'string' ? metadataValue.trim() : '';
};

const getVoidReason = (transaction: SessionTransaction) => {
  const directValue = transaction.void_reason?.trim();
  if (directValue) return directValue;

  const metadataValue = getTransactionMetadataValue(transaction, 'void_reason');
  return typeof metadataValue === 'string' ? metadataValue.trim() : '';
};

const getTransactionExternalReference = (transaction: SessionTransaction) => {
  const directValue = transaction.external_reference?.trim();
  if (directValue) return directValue;

  const metadataValue = getTransactionMetadataValue(
    transaction,
    'external_reference'
  );
  return typeof metadataValue === 'string' || typeof metadataValue === 'number'
    ? metadataValue.toString().trim()
    : '';
};

const hasManualPaymentText = (value: unknown) => {
  const normalizedValue = value?.toString().trim().toLowerCase() ?? '';
  if (!normalizedValue) return false;

  return (
    normalizedValue.includes('manual') ||
    normalizedValue.includes('record payment') ||
    normalizedValue.includes('recorded payment') ||
    normalizedValue.includes('ledger payment')
  );
};

const isManualSessionPaymentTransaction = (transaction: SessionTransaction) => {
  const paymentTypeName = transaction.payment_type?.name;
  const paymentTypeDisplayName = transaction.payment_type?.display_name;

  const directMarkers = [
    transaction.source,
    transaction.display_type,
    paymentTypeName,
    paymentTypeDisplayName,
    getTransactionMetadataValue(transaction, 'source'),
    getTransactionMetadataValue(transaction, 'payment_source'),
    getTransactionMetadataValue(transaction, 'payment_flow'),
    getTransactionMetadataValue(transaction, 'entry_type'),
    getTransactionMetadataValue(transaction, 'manual_payment_source'),
  ];

  return (
    directMarkers.some(hasManualPaymentText) ||
    getTransactionMetadataBooleanValue(transaction, 'is_manual') ||
    getTransactionMetadataBooleanValue(transaction, 'manual_payment') ||
    getTransactionMetadataBooleanValue(transaction, 'is_manual_payment') ||
    getTransactionMetadataBooleanValue(transaction, 'recorded_manually')
  );
};

const getTransactionPaidAt = (transaction: SessionTransaction) => {
  const directValue = transaction.paid_at?.trim();
  if (directValue) return directValue;

  const metadataValue = getTransactionMetadataValue(transaction, 'paid_at');
  return typeof metadataValue === 'string' || typeof metadataValue === 'number'
    ? metadataValue.toString().trim()
    : '';
};

const getTransactionSignedAmount = (transaction: SessionTransaction) => {
  const signedAmount = Number(transaction.signed_amount);
  if (Number.isFinite(signedAmount)) {
    return isRefundTransaction(transaction) || isVoidTransaction(transaction)
      ? -Math.abs(signedAmount)
      : signedAmount;
  }

  const amount = Number(transaction.amount ?? 0);
  return isRefundTransaction(transaction) || isVoidTransaction(transaction)
    ? -Math.abs(amount)
    : amount;
};

const getBookingSessionAmount = (value?: unknown) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const padDatePart = (value: number) => value.toString().padStart(2, '0');

const getLocalDateTimeInputValue = (value: Date = new Date()) => {
  if (Number.isNaN(value.getTime())) return '';

  const year = value.getFullYear();
  const month = padDatePart(value.getMonth() + 1);
  const day = padDatePart(value.getDate());
  const hours = padDatePart(value.getHours());
  const minutes = padDatePart(value.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const getIsoDateTimeFromInputValue = (value: string) => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return undefined;

  const date = new Date(trimmedValue);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const getNumericValue = (value: unknown, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const formatHoursLabel = (value: number) => {
  const roundedValue = Math.round(value * 100) / 100;
  return `${roundedValue}h`;
};

const formatPriceCalculation = (pricePerHour: number, hours: number) =>
  `${toCurrency(pricePerHour)} x ${formatHoursLabel(hours)}`;

const getTransactionBaseAmount = (transaction: SessionTransaction) =>
  Math.abs(getTransactionSignedAmount(transaction));

const getRefundedAmountForTransaction = (
  transaction: SessionTransaction,
  transactions: SessionTransaction[]
) => {
  const originalTransactionId = Number(transaction.id);
  const paymentIntentId = getTransactionPaymentIntent(transaction);

  return transactions.reduce((total, candidate) => {
    if (!isRefundTransaction(candidate) || !isSettledTransaction(candidate)) {
      return total;
    }

    const refundedTransactionId = getRefundedTransactionId(candidate);
    const matchesByTransactionId =
      Number.isFinite(originalTransactionId) &&
      refundedTransactionId === originalTransactionId;
    const matchesByPaymentIntent =
      paymentIntentId.length > 0 &&
      getTransactionPaymentIntent(candidate) === paymentIntentId;

    if (!matchesByTransactionId && !matchesByPaymentIntent) {
      return total;
    }

    return total + Math.abs(getTransactionSignedAmount(candidate));
  }, 0);
};

const getRemainingRefundableAmount = (
  transaction: SessionTransaction,
  transactions: SessionTransaction[]
) => {
  const originalAmount = getTransactionBaseAmount(transaction);
  const refundedAmount = getRefundedAmountForTransaction(
    transaction,
    transactions
  );

  return Math.max(originalAmount - refundedAmount, 0);
};

const getVoidedAmountForTransaction = (
  transaction: SessionTransaction,
  transactions: SessionTransaction[]
) => {
  const originalTransactionId = Number(transaction.id);

  return transactions.reduce((total, candidate) => {
    if (!isVoidTransaction(candidate) || !isSettledTransaction(candidate)) {
      return total;
    }

    const voidedTransactionId = getVoidedTransactionId(candidate);
    const matchesByTransactionId =
      Number.isFinite(originalTransactionId) &&
      voidedTransactionId === originalTransactionId;

    if (!matchesByTransactionId) {
      return total;
    }

    return total + Math.abs(getTransactionSignedAmount(candidate));
  }, 0);
};

const getRemainingVoidableAmount = (
  transaction: SessionTransaction,
  transactions: SessionTransaction[]
) => {
  const originalAmount = getTransactionBaseAmount(transaction);
  const voidedAmount = getVoidedAmountForTransaction(transaction, transactions);

  return Math.max(originalAmount - voidedAmount, 0);
};

const getRemainingPaidAmountForTransaction = (
  transaction: SessionTransaction,
  transactions: SessionTransaction[]
) => {
  if (isRefundTransaction(transaction) || isVoidTransaction(transaction)) {
    return 0;
  }
  if (!isSettledTransaction(transaction)) return 0;

  const paidAmount = getTransactionSignedAmount(transaction);
  if (paidAmount <= 0) return 0;

  const reversedAmount =
    getRefundedAmountForTransaction(transaction, transactions) +
    getVoidedAmountForTransaction(transaction, transactions);

  return Math.max(paidAmount - reversedAmount, 0);
};

const hasDepositText = (value: unknown) =>
  value?.toString().trim().toLowerCase().includes('deposit') ?? false;

const isDepositPaymentTransaction = (transaction: SessionTransaction) =>
  [
    transaction.display_type,
    transaction.payment_type?.name,
    transaction.payment_type?.display_name,
  ].some(hasDepositText);

const canRefundTransaction = (
  transaction: SessionTransaction,
  transactions: SessionTransaction[]
) => {
  if (isRefundTransaction(transaction)) return false;
  if (!isSettledTransaction(transaction)) return false;
  if (getTransactionSignedAmount(transaction) <= 0) return false;
  if (!getTransactionPaymentIntent(transaction)) return false;

  return getRemainingRefundableAmount(transaction, transactions) > 0;
};

const canVoidTransaction = (
  transaction: SessionTransaction,
  transactions: SessionTransaction[]
) => {
  if (isRefundTransaction(transaction) || isVoidTransaction(transaction)) {
    return false;
  }
  if (!isSettledTransaction(transaction)) return false;
  if (getTransactionSignedAmount(transaction) <= 0) return false;
  if (getTransactionPaymentIntent(transaction)) return false;
  if (!isManualSessionPaymentTransaction(transaction)) return false;

  return getRemainingVoidableAmount(transaction, transactions) > 0;
};

type CustomPaymentTypeOption = {
  id: number;
  label: string;
};

const CUSTOM_PAYMENT_TYPE_OPTIONS: CustomPaymentTypeOption[] = [
  { id: 1, label: 'Deposit' },
  { id: 2, label: 'Remaining amount' },
  { id: 3, label: 'Full amount' },
  { id: 4, label: 'Partial amount' },
  { id: 6, label: 'Extra time' },
  { id: 10, label: 'Linen' },
  { id: 5, label: 'Tip' },
  { id: 9, label: 'Other' },
];

const MANUAL_PAYMENT_DEFAULT_SOURCE = 'Manual Payment';

const HOLD_STATUS_KEYS = new Set([
  'hold',
  'held',
  'onhold',
  'on_hold',
  'payment_hold',
  'payment_on_hold',
]);

const normalizeHoldStatusKey = (value: unknown) =>
  value
    ?.toString()
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_') ?? '';

const isHoldStatusValue = (value: unknown) => {
  const normalized = normalizeHoldStatusKey(value);
  if (!normalized) return false;

  if (/^-?\d+$/.test(normalized)) {
    return getRawTransactionStatusKey(normalized) === 'onhold';
  }

  return HOLD_STATUS_KEYS.has(normalized);
};

const isActiveHoldTransaction = (transaction: SessionTransaction) =>
  isHoldStatusValue(transaction.status) ||
  isHoldStatusValue(getTransactionStatusPresentation(transaction).label);

const mergeRecordedPaymentSession = (
  response:
    | RecordBookingSessionPaymentResponse
    | VoidBookingSessionPaymentResponse,
  fallbackSession?: BookingSession
): BookingSession | null => {
  const baseSession = response.booking_session ?? fallbackSession;
  if (!baseSession) return null;

  const { summary, transaction } = response;
  const existingTransactions = baseSession.payment_transaction ?? [];
  const paymentTransaction = transaction
    ? existingTransactions.some((item) => item.id === transaction.id)
      ? existingTransactions.map((item) =>
          item.id === transaction.id ? transaction : item
        )
      : [transaction, ...existingTransactions]
    : existingTransactions;

  return {
    ...baseSession,
    ...(summary
      ? {
          paid_amount: summary.paid_amount,
          refunded_amount: summary.refunded_amount,
          outstanding_balance: summary.outstanding_balance,
        }
      : {}),
    billing: summary
      ? {
          ...(baseSession.billing ?? {}),
          paid_amount: summary.paid_amount,
          refunded_amount: summary.refunded_amount,
          outstanding_balance: summary.outstanding_balance,
        }
      : baseSession.billing,
    payment_transaction: paymentTransaction,
  };
};

const removeTransactionFromSession = (
  session: BookingSession | null | undefined,
  transactionId: number
): BookingSession | null => {
  if (!session) return null;

  return {
    ...session,
    payment_transaction: (session.payment_transaction ?? []).filter(
      (transaction) => transaction.id !== transactionId
    ),
  };
};

const updateBookingSessionCacheValue = (
  value: unknown,
  updatedSession: BookingSession
): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) =>
      item?.id === updatedSession.id ? updatedSession : item
    );
  }

  if (!value || typeof value !== 'object') return value;

  const record = value as Record<string, unknown>;
  const nextRecord = { ...record };
  let changed = false;

  ['data', 'booking_sessions'].forEach((key) => {
    if (!Array.isArray(record[key])) return;

    nextRecord[key] = (record[key] as unknown[]).map((item) =>
      (item as BookingSession | undefined)?.id === updatedSession.id
        ? updatedSession
        : item
    );
    changed = true;
  });

  return changed ? nextRecord : value;
};

const getErrorMessageText = (error: unknown) => {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (!error || typeof error !== 'object') return '';

  const record = error as Record<string, unknown>;
  const message = record.message ?? record.error ?? record.detail;
  if (typeof message === 'string') return message;
  if (Array.isArray(message)) return message.join(' ');

  return JSON.stringify(message ?? error);
};

const getRecordPaymentErrorMessage = (error: unknown) => {
  const message = getErrorMessageText(error).toLowerCase();

  if (
    message.includes('overpayment') ||
    message.includes('over payment') ||
    message.includes('exceed') ||
    message.includes('outstanding balance') ||
    message.includes('greater than outstanding')
  ) {
    return 'Payment amount cannot exceed the outstanding balance.';
  }

  if (message.includes('hold') || message.includes('onhold')) {
    return 'A payment hold is active for this session.';
  }

  if (
    message.includes('payment_type') ||
    message.includes('payment type') ||
    message.includes('paymenttype')
  ) {
    return 'Select a valid payment type before recording the payment.';
  }

  return 'Failed to record payment.';
};

const getVoidPaymentErrorMessage = (error: unknown) => {
  const message = getErrorMessageText(error).toLowerCase();

  if (
    message.includes('stripe') ||
    message.includes('payment intent') ||
    message.includes('payment_intent')
  ) {
    return 'Stripe-backed transactions cannot be voided.';
  }

  if (
    message.includes('exceed') ||
    message.includes('greater than') ||
    message.includes('voidable') ||
    message.includes('remaining balance') ||
    message.includes('remaining amount')
  ) {
    return 'Void amount cannot exceed the remaining voidable balance.';
  }

  if (message.includes('reason')) {
    return 'Enter a reason for voiding this payment.';
  }

  return 'Failed to void payment.';
};

type VBMPaymentDetailsProps = {
  bookingSession?: BookingSession;
  bookingId?: number;
  walletOptions?: BookingWallet[];
};

export default function VBMPaymentDetails({
  bookingSession: bookingSessionProp,
  bookingId,
  walletOptions = [],
}: VBMPaymentDetailsProps) {
  const downMd = useResponsive('down', 'md');
  const { isSuperAdmin } = useAuth();
  const { successMsg, errorMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const serviceExtras = useSelector(
    (state) => state.booking.all_extras_from_api
  );
  const sessionParam = searchParams.get('sessionID');
  const [localBookingSession, setLocalBookingSession] =
    useState<BookingSession | null>(null);
  const bookingSession = localBookingSession ?? bookingSessionProp;

  useEffect(() => {
    setLocalBookingSession(null);
  }, [bookingSessionProp?.id]);

  const transactions = useMemo(() => {
    const items = bookingSession?.payment_transaction ?? [];
    return [...items].sort((a, b) => {
      const parseTimestamp = (value?: string | null) => {
        if (!value) return 0;
        const timestamp = Date.parse(value);
        return Number.isFinite(timestamp) ? timestamp : 0;
      };
      return parseTimestamp(b.created_at) - parseTimestamp(a.created_at);
    });
  }, [bookingSession?.payment_transaction]);

  const transactionDate = transactions.length
    ? fDateTime(transactions[0]?.created_at ?? '')
    : 'No transaction';

  const billing = bookingSession?.billing;
  const sessionPricePerHour = getSessionHourlyRate(bookingSession);
  const isRawPricePerHourMissing = sessionPricePerHour === null;
  const pricePerHour = sessionPricePerHour ?? 0;
  const baseHours = getNumericValue(
    billing?.cleaning_hours ??
      bookingSession?.cleaning_hours ??
      bookingSession?.hours
  );
  const extraHours = getNumericValue(
    billing?.extra_hours ?? bookingSession?.extra_hours
  );
  const bookingItemsCleaningExtrasTotal = (
    bookingSession?.booking_items ?? []
  ).reduce((total, item) => total + getNumericValue(item.total), 0);
  const selectedExtraIds = transformExtraIds(bookingSession?.extras);
  const selectedExtraObjects = retrieveExtrasFromId(
    selectedExtraIds,
    serviceExtras
  );
  const cleaningExtrasHoursFromSettings = selectedExtraObjects.reduce(
    (total, item) => total + getNumericValue(item.hours),
    0
  );
  const billingCleaningExtrasHours = getBookingSessionAmount(
    billing?.extras_hours ?? bookingSession?.extras_hours
  );
  const cleaningExtrasHours =
    billingCleaningExtrasHours ??
    (cleaningExtrasHoursFromSettings > 0
      ? cleaningExtrasHoursFromSettings
      : pricePerHour > 0
      ? bookingItemsCleaningExtrasTotal / pricePerHour
      : 0);
  const calculatedCleaningExtrasTotal =
    pricePerHour > 0 && cleaningExtrasHours > 0
      ? pricePerHour * cleaningExtrasHours
      : null;
  const cleaningExtrasTotal =
    calculatedCleaningExtrasTotal ??
    getBookingSessionAmount(
      billing?.cleaning_extras_amount ?? bookingSession?.cleaning_extras_amount
    ) ??
    bookingItemsCleaningExtrasTotal;
  const totalHours = resolveBookingSessionBillableHours(bookingSession);
  const baseHoursAmount =
    getBookingSessionAmount(
      billing?.cleaning_amount ?? bookingSession?.cleaning_amount
    ) ?? (pricePerHour > 0 && baseHours > 0 ? pricePerHour * baseHours : 0);
  const extraHoursAmount =
    getBookingSessionAmount(
      billing?.extra_hours_amount ?? bookingSession?.extra_hours_amount
    ) ?? (pricePerHour > 0 && extraHours > 0 ? pricePerHour * extraHours : 0);
  const cleaningProductsTotal =
    getBookingSessionAmount(
      billing?.cleaning_products_amount ??
        bookingSession?.cleaning_products_amount
    ) ??
    (bookingSession?.cleaning_products
      ? getNumericValue(bookingSession?.cleaning_products_price)
      : 0);
  const additionalServicesFallback = (
    bookingSession?.additional_services ?? []
  ).reduce((total, service) => total + getNumericValue(service.price), 0);
  const additionalServicesTotal =
    getBookingSessionAmount(
      billing?.additional_services_amount ??
        bookingSession?.additional_services_amount
    ) ?? additionalServicesFallback;
  const additionalServicesNames = (bookingSession?.additional_services ?? [])
    .map((service) => service.name?.trim())
    .filter(Boolean)
    .join(', ');
  const totalAmountApiValue = getBookingSessionAmount(
    billing?.total_amount ?? bookingSession?.total_amount
  );
  const totalAmountFallback =
    baseHoursAmount +
    extraHoursAmount +
    cleaningExtrasTotal +
    cleaningProductsTotal +
    additionalServicesTotal;
  const totalAmountBaseValue = totalAmountApiValue ?? totalAmountFallback;
  const totalAmountValue = Math.max(totalAmountBaseValue, totalAmountFallback);
  const totalAmountApiAdjustment = Math.max(
    totalAmountValue - totalAmountBaseValue,
    0
  );

  const settledTransactions = useMemo(
    () =>
      transactions.filter((transaction) => isSettledTransaction(transaction)),
    [transactions]
  );
  const hasTransactionLedger = transactions.length > 0;

  const totalPaidFromTransactions = settledTransactions.reduce(
    (total, transaction) =>
      total + getRemainingPaidAmountForTransaction(transaction, transactions),
    0
  );

  const totalAfterDiscountApiValue = getBookingSessionAmount(
    billing?.total_amount_after_discount ??
      bookingSession?.total_amount_after_discount
  );
  const totalAfterDiscountValue =
    totalAfterDiscountApiValue !== null
      ? Math.min(
          Math.max(totalAfterDiscountApiValue + totalAmountApiAdjustment, 0),
          totalAmountValue
        )
      : totalAmountValue;
  const activeCouponCode = bookingSession?.coupon?.code?.trim() || '';
  const hasCouponApplied = Boolean(
    bookingSession?.coupon_code_id && activeCouponCode
  );
  const derivedCouponDiscount = useMemo(() => {
    if (!hasCouponApplied) return 0;

    const couponValue = Number(bookingSession?.coupon?.value ?? 0);
    if (!Number.isFinite(couponValue) || couponValue <= 0) {
      return 0;
    }

    const couponType = bookingSession?.coupon?.value_type
      ?.toString()
      .toLowerCase()
      .trim();

    if (couponType === 'percentage') {
      return Math.min((totalAmountValue * couponValue) / 100, totalAmountValue);
    }

    return Math.min(couponValue, totalAmountValue);
  }, [
    bookingSession?.coupon?.value,
    bookingSession?.coupon?.value_type,
    hasCouponApplied,
    totalAmountValue,
  ]);
  const discountValueRaw = Math.max(
    totalAmountValue - totalAfterDiscountValue,
    getNumericValue(
      billing?.total_amount_discount ?? bookingSession?.total_amount_discount
    ),
    derivedCouponDiscount,
    0
  );
  const appliedDiscountValue = hasCouponApplied ? discountValueRaw : 0;

  const couponValueLabel =
    hasCouponApplied && appliedDiscountValue > 0
      ? toCurrency(appliedDiscountValue)
      : null;

  const depositPaidFromTransactions = settledTransactions
    .filter(isDepositPaymentTransaction)
    .reduce(
      (total, transaction) =>
        total + getRemainingPaidAmountForTransaction(transaction, transactions),
      0
    );
  const depositPaidApiValue = getBookingSessionAmount(
    billing?.deposit_paid ?? bookingSession?.deposit_paid
  );
  const balancePaidApiValue = getBookingSessionAmount(
    billing?.paid_amount ?? bookingSession?.paid_amount
  );
  const totalPaidFromApi =
    Math.max(depositPaidApiValue ?? 0, 0) +
    Math.max(balancePaidApiValue ?? 0, 0);
  const outstandingFallback = Math.max(
    totalAmountValue - appliedDiscountValue - totalPaidFromTransactions,
    0
  );
  const outstandingApiFallback = Math.max(
    totalAmountValue - appliedDiscountValue - totalPaidFromApi,
    0
  );
  const outstandingApiValue = getBookingSessionAmount(
    billing?.outstanding_balance ?? bookingSession?.outstanding_balance
  );
  const adjustedOutstandingApiValue =
    outstandingApiValue !== null
      ? Math.max(
          outstandingApiValue +
            Math.min(
              totalAmountApiAdjustment,
              Math.max(outstandingApiFallback - outstandingApiValue, 0)
            ),
          0
        )
      : null;
  const depositPaidValue = hasTransactionLedger
    ? depositPaidFromTransactions
    : Math.max(depositPaidApiValue ?? 0, 0);
  const balancePaidValue = hasTransactionLedger
    ? Math.max(totalPaidFromTransactions - depositPaidValue, 0)
    : Math.max(balancePaidApiValue ?? 0, 0);
  const outstandingValue = hasTransactionLedger
    ? outstandingFallback
    : adjustedOutstandingApiValue ?? outstandingApiFallback;
  const hasOutstandingBalance = outstandingValue > 0;

  const rawBookingWallet = bookingSession?.booking?.wallet;
  const bookingWallet = useMemo<BookingWallet | null>(() => {
    if (rawBookingWallet && typeof rawBookingWallet === 'object') {
      return rawBookingWallet as BookingWallet;
    }
    return null;
  }, [rawBookingWallet]);

  const [editAnchorEl, setEditAnchorEl] = useState<HTMLElement | null>(null);
  const isEditing = Boolean(editAnchorEl);
  const [selectedWalletId, setSelectedWalletId] = useState<number | null>(null);
  const [savedWalletId, setSavedWalletId] = useState<number | null>(
    () => bookingWallet?.id ?? null
  );
  const [savedStripeId, setSavedStripeId] = useState<string | null>(
    () => bookingSession?.booking?.payment_method ?? null
  );
  const [isTransactionsDialogOpen, setTransactionsDialogOpen] = useState(false);
  const [expandedTransactionId, setExpandedTransactionId] = useState<
    number | null
  >(null);
  const [isRefundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedRefundTransactionId, setSelectedRefundTransactionId] =
    useState<number | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState<string>(
    REFUND_REASON_OPTIONS[0].value
  );
  const [refundDescription, setRefundDescription] = useState('');
  const [isVoidPaymentDialogOpen, setVoidPaymentDialogOpen] = useState(false);
  const [selectedVoidTransactionId, setSelectedVoidTransactionId] = useState<
    number | null
  >(null);
  const [voidPaymentType, setVoidPaymentType] =
    useState<VoidPaymentType>('full');
  const [voidPaymentAmount, setVoidPaymentAmount] = useState('');
  const [voidPaymentReason, setVoidPaymentReason] = useState('');
  const [voidPaymentDescription, setVoidPaymentDescription] = useState('');
  const [voidPaymentSubmitted, setVoidPaymentSubmitted] = useState(false);
  const [voidPaymentFormError, setVoidPaymentFormError] = useState('');
  const [transactionToDelete, setTransactionToDelete] =
    useState<SessionTransaction | null>(null);
  const [deleteTransactionError, setDeleteTransactionError] = useState('');
  const sessionIdentifier =
    bookingSession?.id ?? bookingSession?.booking_id ?? bookingId ?? null;
  const previousSessionRef = useRef(sessionIdentifier);
  const pendingWalletIdRef = useRef<number | null>(null);
  const [isCouponDialogOpen, setCouponDialogOpen] = useState(false);
  const [couponCodeInput, setCouponCodeInput] = useState('');
  const [applyToAllFutureSessions, setApplyToAllFutureSessions] =
    useState(false);
  const [isCustomChargeDialogOpen, setCustomChargeDialogOpen] = useState(false);
  const [customChargeAmount, setCustomChargeAmount] = useState('');
  const [customChargePaymentTypeId, setCustomChargePaymentTypeId] = useState<
    number | ''
  >('');
  const [isPaymentHoldDialogOpen, setPaymentHoldDialogOpen] = useState(false);
  const [isPaymentLinkDialogOpen, setPaymentLinkDialogOpen] = useState(false);
  const [paymentLinkAmount, setPaymentLinkAmount] = useState('');
  const [paymentLinkDescription, setPaymentLinkDescription] = useState('');
  const [paymentLinkPaymentTypeId, setPaymentLinkPaymentTypeId] = useState<
    number | ''
  >('');
  const [isRecordPaymentDialogOpen, setRecordPaymentDialogOpen] =
    useState(false);
  const [recordPaymentAmount, setRecordPaymentAmount] = useState('');
  const [recordPaymentPaymentTypeId, setRecordPaymentPaymentTypeId] = useState<
    number | ''
  >('');
  const [recordPaymentSource, setRecordPaymentSource] = useState(
    MANUAL_PAYMENT_DEFAULT_SOURCE
  );
  const [recordPaymentExternalReference, setRecordPaymentExternalReference] =
    useState('');
  const [recordPaymentDescription, setRecordPaymentDescription] = useState('');
  const [recordPaymentPaidAt, setRecordPaymentPaidAt] = useState('');
  const [recordPaymentSubmitted, setRecordPaymentSubmitted] = useState(false);
  const [recordPaymentFormError, setRecordPaymentFormError] = useState('');
  const [isPaymentMethodRequestDialogOpen, setPaymentMethodRequestDialogOpen] =
    useState(false);
  const [isRepriceDialogOpen, setRepriceDialogOpen] = useState(false);
  const [isRepriceConfirmDialogOpen, setRepriceConfirmDialogOpen] =
    useState(false);
  const [repriceRateInput, setRepriceRateInput] = useState('');
  const [repriceReason, setRepriceReason] = useState('');
  const [repriceApplyToAllFuture, setRepriceApplyToAllFuture] = useState(false);
  const [repricePreviewRows, setRepricePreviewRows] = useState<{
    eligible: RepricePreviewRow[];
    skipped: RepricePreviewRow[];
  } | null>(null);

  const customerId =
    bookingSession?.user?.id ?? bookingSession?.booking?.user?.id;

  const { data: bookingUserData } = useQuery({
    queryKey: ['booking-user', customerId],
    queryFn: () => getSingleUser(Number(customerId)),
    enabled: Boolean(customerId),
  });

  const bookingSessionId = bookingSession?.id ?? null;
  const serviceVariantId = bookingSession?.service_variant?.id ?? null;

  const { data: adminCoupons = [], isLoading: isAdminCouponsLoading } =
    useQuery({
      queryKey: ['coupons', 'admin-dashboard'],
      queryFn: getCoupons,
      select: (coupons) =>
        coupons
          .filter((coupon) =>
            (coupon.coupon_platforms ?? []).some(
              (platform) =>
                platform.name?.trim().toLowerCase() === 'admin dashboard'
            )
          )
          .filter((coupon) => {
            if (!coupon.user_id) return true;
            if (!customerId) return false;
            return Number(coupon.user_id) === Number(customerId);
          })
          .filter((coupon) => {
            if (!coupon.active) return false;
            const now = Date.now();
            const startOk = coupon.start_date
              ? Date.parse(coupon.start_date) <= now
              : true;
            const endOk = coupon.end_date
              ? Date.parse(coupon.end_date) >= now
              : true;
            return startOk && endOk;
          })
          .sort((a, b) => a.code.localeCompare(b.code)),
      enabled: Boolean(bookingSessionId) && isCouponDialogOpen,
    });

  const mergedWallets = useMemo(() => {
    const merged: BookingWallet[] = [];

    const normaliseWallet = (
      wallet?: AnyWallet | null
    ): BookingWallet | null => {
      if (!wallet) return null;
      return {
        id: wallet.id,
        stripe_id: wallet.stripe_id,
        user_id: wallet.user_id ?? 0,
        name: wallet.name ?? '',
        brand: wallet.brand ?? '',
        country: (wallet as any).country ?? '',
        exp_month: wallet.exp_month,
        exp_year: wallet.exp_year,
        last4: wallet.last4,
        type: wallet.type,
        default: wallet.default,
        last_used: wallet.last_used,
        created_at: wallet.created_at,
        updated_at: wallet.updated_at,
      };
    };

    const appendUnique = (wallet?: AnyWallet | null) => {
      const normalised = normaliseWallet(wallet);
      if (!normalised) return;

      const stripeIdentity = getWalletStripeIdentity(normalised);
      const displayIdentity = getWalletDisplayIdentity(normalised);
      const existingIndex = merged.findIndex((item) => {
        if (item.id === normalised.id) {
          return true;
        }

        if (
          stripeIdentity &&
          getWalletStripeIdentity(item) === stripeIdentity
        ) {
          return true;
        }

        return (
          Boolean(displayIdentity) &&
          getWalletDisplayIdentity(item) === displayIdentity
        );
      });

      if (existingIndex >= 0) {
        merged[existingIndex] = mergeWalletDetails(
          merged[existingIndex],
          normalised
        );
      } else {
        merged.push(normalised);
      }
    };

    (walletOptions ?? []).forEach(appendUnique);
    appendUnique(bookingWallet);
    (bookingUserData?.wallet ?? []).forEach(appendUnique);

    transactions.forEach((transaction) => {
      appendUnique(transaction.wallet as AnyWallet | undefined);
    });

    const toTimestamp = (value?: string | number | Date | null) => {
      if (!value) return 0;
      const timestamp = new Date(value).getTime();
      return Number.isFinite(timestamp) ? timestamp : 0;
    };

    return [...merged].sort((a, b) => {
      const bLastUsed = toTimestamp(b.last_used);
      const aLastUsed = toTimestamp(a.last_used);

      if (bLastUsed !== aLastUsed) {
        return bLastUsed - aLastUsed;
      }

      if (b.default !== a.default) {
        return Number(b.default) - Number(a.default);
      }

      return toTimestamp(b.updated_at) - toTimestamp(a.updated_at);
    });
  }, [walletOptions, bookingWallet, transactions, bookingUserData?.wallet]);

  const currentStripeId = bookingSession?.booking?.payment_method ?? null;

  useEffect(() => {
    const sessionChanged = previousSessionRef.current !== sessionIdentifier;
    const canonicalWallet =
      findWalletByIdentifiers(mergedWallets, {
        id: bookingWallet?.id,
        stripeId: currentStripeId ?? bookingWallet?.stripe_id,
      }) ?? bookingWallet;

    const derivedWalletId =
      canonicalWallet?.id ??
      findWalletByIdentifiers(mergedWallets, {
        stripeId: currentStripeId,
      })?.id ??
      null;

    const derivedStripeId =
      currentStripeId ?? canonicalWallet?.stripe_id ?? null;

    if (sessionChanged) {
      previousSessionRef.current = sessionIdentifier;
      setSavedWalletId(derivedWalletId);
      setSavedStripeId(derivedStripeId);
      pendingWalletIdRef.current = null;
      return;
    }

    if (isEditing) return;

    if (
      typeof derivedWalletId === 'number' &&
      derivedWalletId !== savedWalletId
    ) {
      setSavedWalletId(derivedWalletId);
      if (pendingWalletIdRef.current === derivedWalletId) {
        pendingWalletIdRef.current = null;
      }
    }

    if (
      derivedWalletId === null &&
      savedWalletId !== null &&
      !pendingWalletIdRef.current
    ) {
      setSavedWalletId(null);
    }

    if (derivedStripeId !== savedStripeId) {
      setSavedStripeId(derivedStripeId ?? null);
    }
  }, [
    bookingWallet?.id,
    bookingWallet?.stripe_id,
    currentStripeId,
    mergedWallets,
    savedWalletId,
    savedStripeId,
    sessionIdentifier,
    isEditing,
  ]);

  useEffect(() => {
    if (!isCouponDialogOpen) {
      setCouponCodeInput(activeCouponCode);
      setApplyToAllFutureSessions(false);
    }
  }, [activeCouponCode, isCouponDialogOpen]);

  const resolvedWallet = useMemo(() => {
    if (savedWalletId || savedStripeId) {
      const match = findWalletByIdentifiers(mergedWallets, {
        id: savedWalletId,
        stripeId: savedStripeId,
      });
      if (match) return match;
      if (bookingWallet) {
        if (
          (savedWalletId && bookingWallet.id === savedWalletId) ||
          (savedStripeId && bookingWallet.stripe_id === savedStripeId)
        ) {
          return bookingWallet;
        }
      }
    }

    if (bookingWallet) {
      return (
        findWalletByIdentifiers(mergedWallets, {
          id: bookingWallet.id,
          stripeId: bookingWallet.stripe_id,
        }) ?? bookingWallet
      );
    }

    return null;
  }, [savedWalletId, savedStripeId, mergedWallets, bookingWallet]);

  const stripeReference = savedStripeId ?? currentStripeId ?? null;
  const maskedStripeReference = maskPaymentMethodId(stripeReference);
  const fallbackWalletLabel = resolvedWallet
    ? ''
    : savedWalletId
    ? `Wallet #${savedWalletId}`
    : maskedStripeReference
    ? `Saved payment method (${maskedStripeReference})`
    : stripeReference
    ? 'Saved payment method'
    : '';

  const paymentMethodDisplay: ReactNode = resolvedWallet ? (
    <WalletSummary wallet={resolvedWallet} />
  ) : fallbackWalletLabel ? (
    fallbackWalletLabel
  ) : (
    'No payment method set'
  );
  const resolvedWalletDisplay = useMemo(
    () => (resolvedWallet ? buildWalletDisplay(resolvedWallet) : null),
    [resolvedWallet]
  );
  const hasAttachedPaymentMethod = Boolean(resolvedWallet || stripeReference);
  const hasPaymentMethodForCharge = hasAttachedPaymentMethod;
  const chargeMethodLabel = resolvedWalletDisplay?.primary
    ? resolvedWalletDisplay.primary
    : maskedStripeReference
    ? `saved payment method (${maskedStripeReference})`
    : '';

  const couponDescription = bookingSession?.coupon?.description?.trim();
  const couponLabelText = hasCouponApplied
    ? `Coupon ${activeCouponCode}${
        couponValueLabel ? ` · ${couponValueLabel}` : ''
      }`
    : 'No coupon applied';

  const couponMetaNode =
    couponDescription && hasCouponApplied ? (
      <Tooltip title={couponDescription} placement="top">
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ cursor: 'help' }}
        >
          {couponLabelText}
        </Typography>
      </Tooltip>
    ) : (
      <Typography variant="caption" color="text.secondary">
        {couponLabelText}
      </Typography>
    );

  const discountDisplay = (
    <Stack alignItems="flex-end" spacing={0.25}>
      <Typography variant="body2" fontWeight={600}>
        {appliedDiscountValue > 0
          ? toSignedCurrency(-appliedDiscountValue)
          : toCurrency(0)}
      </Typography>
      {couponMetaNode}
    </Stack>
  );

  const pricePerHourDisplay = isRawPricePerHourMissing
    ? '—'
    : toCurrency(pricePerHour);

  const totalAmountDisplay = (
    <Stack alignItems="flex-end" spacing={0.35}>
      <Typography variant="body2" fontWeight={600}>
        {toCurrency(totalAmountValue)}
      </Typography>

      {baseHoursAmount > 0 && (
        <Typography variant="caption" color="text.secondary">
          Cleaning: {formatPriceCalculation(pricePerHour, baseHours)} ={' '}
          {toCurrency(baseHoursAmount)}
        </Typography>
      )}

      {extraHoursAmount > 0 && (
        <Typography variant="caption" color="text.secondary">
          Extra hours: {formatPriceCalculation(pricePerHour, extraHours)} ={' '}
          {toCurrency(extraHoursAmount)}
        </Typography>
      )}

      {cleaningExtrasTotal > 0 && (
        <Typography variant="caption" color="text.secondary">
          Cleaning extras:{' '}
          {formatPriceCalculation(pricePerHour, cleaningExtrasHours)} ={' '}
          {toCurrency(cleaningExtrasTotal)}
        </Typography>
      )}

      {cleaningProductsTotal > 0 && (
        <Typography variant="caption" color="text.secondary">
          Cleaning products: {toCurrency(cleaningProductsTotal)}
        </Typography>
      )}

      {additionalServicesTotal > 0 && (
        <Typography variant="caption" color="text.secondary">
          Extra services
          {additionalServicesNames ? ` (${additionalServicesNames})` : ''}:{' '}
          {toCurrency(additionalServicesTotal)}
        </Typography>
      )}

      {totalHours > 0 && (
        <Typography variant="caption" color="text.secondary">
          Total hours: {formatHoursLabel(totalHours)}
        </Typography>
      )}
    </Stack>
  );

  const transactionDetails: { label: string; value: ReactNode }[] = [
    { label: 'Last Transaction Date', value: transactionDate },
    { label: 'Payment Method', value: paymentMethodDisplay },
    { label: 'Price per Hour', value: pricePerHourDisplay },
    { label: 'Total Amount', value: totalAmountDisplay },
    { label: 'Discount', value: discountDisplay },
    { label: 'Deposit Paid', value: toCurrency(depositPaidValue) },
    { label: 'Balance Paid', value: toCurrency(balancePaidValue) },
    { label: 'Outstanding Balance', value: toCurrency(outstandingValue) },
  ];

  const canEdit = Boolean(
    bookingId && mergedWallets.length > 0 && bookingSession
  );
  const canManageCoupons = Boolean(bookingSessionId);
  const hasTransactions = transactions.length > 0;
  const selectedRefundTransaction = useMemo(
    () =>
      transactions.find(
        (transaction) => transaction.id === selectedRefundTransactionId
      ) ?? null,
    [selectedRefundTransactionId, transactions]
  );
  const selectedRefundRemainingAmount = selectedRefundTransaction
    ? getRemainingRefundableAmount(selectedRefundTransaction, transactions)
    : 0;
  const selectedRefundCurrency =
    selectedRefundTransaction?.currency?.trim().toUpperCase() || 'GBP';
  const selectedRefundStatusLabel = selectedRefundTransaction
    ? getTransactionStatusPresentation(selectedRefundTransaction).label
    : '';
  const selectedVoidTransaction = useMemo(
    () =>
      transactions.find(
        (transaction) => transaction.id === selectedVoidTransactionId
      ) ?? null,
    [selectedVoidTransactionId, transactions]
  );
  const selectedVoidRemainingAmount = selectedVoidTransaction
    ? getRemainingVoidableAmount(selectedVoidTransaction, transactions)
    : 0;
  const selectedVoidCurrency =
    selectedVoidTransaction?.currency?.trim().toUpperCase() || 'GBP';
  const selectedVoidStatusLabel = selectedVoidTransaction
    ? getTransactionStatusPresentation(selectedVoidTransaction).label
    : '';

  const handleOpenEdit = (event: MouseEvent<HTMLElement>) => {
    const currentWallet = findWalletByIdentifiers(mergedWallets, {
      id: savedWalletId,
      stripeId: stripeReference,
    });
    const derivedId = currentWallet?.id ?? savedWalletId ?? null;

    setSelectedWalletId(derivedId);
    setEditAnchorEl(event.currentTarget);
  };

  const closeEdit = () => {
    setEditAnchorEl(null);
    setSelectedWalletId(null);
  };

  const handleOpenTransactionsDialog = () => {
    if (!hasTransactions) return;
    // setExpandedTransactionId(transactions[0]?.id ?? null);
    setTransactionsDialogOpen(true);
  };

  const handleCloseTransactionsDialog = () => {
    setTransactionsDialogOpen(false);
    setExpandedTransactionId(null);
  };

  const handleToggleTransaction = (transactionId: number) => {
    setExpandedTransactionId((previous) =>
      previous === transactionId ? null : transactionId
    );
  };

  const handleOpenRefundDialog = (transaction: SessionTransaction) => {
    if (!canRefundTransaction(transaction, transactions)) return;

    const refundableAmount = getRemainingRefundableAmount(
      transaction,
      transactions
    );
    setSelectedRefundTransactionId(transaction.id ?? null);
    setRefundAmount(refundableAmount > 0 ? refundableAmount.toFixed(2) : '');
    setRefundReason(REFUND_REASON_OPTIONS[0].value);
    setRefundDescription('');
    setRefundDialogOpen(true);
  };

  const handleCloseRefundDialog = () => {
    setRefundDialogOpen(false);
    setSelectedRefundTransactionId(null);
    setRefundAmount('');
    setRefundReason(REFUND_REASON_OPTIONS[0].value);
    setRefundDescription('');
  };

  const handleOpenVoidPaymentDialog = (transaction: SessionTransaction) => {
    if (!canVoidTransaction(transaction, transactions)) return;

    const voidableAmount = getRemainingVoidableAmount(
      transaction,
      transactions
    );
    setSelectedVoidTransactionId(transaction.id ?? null);
    setVoidPaymentType('full');
    setVoidPaymentAmount(voidableAmount > 0 ? voidableAmount.toFixed(2) : '');
    setVoidPaymentReason('');
    setVoidPaymentDescription('');
    setVoidPaymentSubmitted(false);
    setVoidPaymentFormError('');
    setVoidPaymentDialogOpen(true);
  };

  const handleCloseVoidPaymentDialog = () => {
    if (voidPaymentMutation.isLoading) return;
    setVoidPaymentDialogOpen(false);
    setSelectedVoidTransactionId(null);
    setVoidPaymentType('full');
    setVoidPaymentAmount('');
    setVoidPaymentReason('');
    setVoidPaymentDescription('');
    setVoidPaymentSubmitted(false);
    setVoidPaymentFormError('');
  };

  const handleOpenDeleteTransactionDialog = (
    transaction: SessionTransaction
  ) => {
    if (isSuperAdmin !== true || !transaction.id) return;
    setDeleteTransactionError('');
    setTransactionToDelete(transaction);
  };

  const handleCloseDeleteTransactionDialog = () => {
    if (deleteTransactionMutation.isLoading) return;
    setDeleteTransactionError('');
    setTransactionToDelete(null);
  };

  const handleConfirmDeleteTransaction = () => {
    const transactionId = transactionToDelete?.id;
    if (
      isSuperAdmin !== true ||
      !transactionId ||
      deleteTransactionMutation.isLoading
    ) {
      return;
    }

    setDeleteTransactionError('');
    deleteTransactionMutation.mutate(transactionId);
  };

  const invalidatePaymentQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['bookingSessions'],
      exact: false,
    });
    queryClient.invalidateQueries({ queryKey: ['bookings'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['transactions'], exact: false });
    if (bookingSessionId) {
      queryClient.invalidateQueries({
        queryKey: ['bookingSession', String(bookingSessionId)],
        exact: true,
      });
    }
    if (sessionParam) {
      queryClient.invalidateQueries({
        queryKey: ['bookingSession', sessionParam],
        exact: true,
      });
    }
  }, [bookingSessionId, queryClient, sessionParam]);

  const writeBookingSessionPaymentCache = useCallback(
    (updatedSession: BookingSession) => {
      const updatedSessionId = String(updatedSession.id);

      queryClient.setQueryData(
        ['bookingSession', updatedSessionId],
        updatedSession
      );

      if (sessionParam === updatedSessionId) {
        queryClient.setQueryData(
          ['bookingSession', sessionParam],
          updatedSession
        );
      }

      queryClient.setQueriesData(
        { queryKey: ['bookingSessions'], exact: false },
        (currentValue) =>
          updateBookingSessionCacheValue(currentValue, updatedSession)
      );
    },
    [queryClient, sessionParam]
  );

  const deleteTransactionMutation = useMutation({
    mutationFn: (transactionId: number) => deleteTransaction(transactionId),
    onSuccess: (_, transactionId) => {
      const updatedSession = removeTransactionFromSession(
        bookingSession,
        transactionId
      );

      if (updatedSession) {
        setLocalBookingSession(updatedSession);
        writeBookingSessionPaymentCache(updatedSession);
      }

      setExpandedTransactionId((previous) =>
        previous === transactionId ? null : previous
      );
      setDeleteTransactionError('');
      setTransactionToDelete(null);
      successMsg('Transaction deleted');
      invalidatePaymentQueries();
    },
    onError: (error) => {
      setDeleteTransactionError('Failed to delete transaction');
      errorMsg(error, 'Failed to delete transaction');
    },
  });

  const applySessionCouponMutation = useMutation({
    mutationFn: ({ couponId }: { couponId: number }) => {
      if (!bookingSessionId) throw new Error('Missing booking session id');
      return updateBookingSession(bookingSessionId, {
        coupon_code_id: couponId,
      });
    },
    onSuccess: (_, variables) => {
      successMsg('Coupon applied to this session');
      setCouponCodeInput(
        adminCoupons.find((coupon) => coupon.id === variables.couponId)?.code ??
          couponCodeInput
      );
      setCouponDialogOpen(false);
      invalidatePaymentQueries();
    },
    onError: (error) => errorMsg(error, 'Failed to apply coupon'),
  });

  const applyBookingCouponMutation = useMutation({
    mutationFn: ({ code }: { code: string }) => {
      if (!bookingId) throw new Error('Missing booking id');
      return applyCouponToBooking(bookingId, code);
    },
    onSuccess: (_, variables) => {
      successMsg('Coupon applied to upcoming sessions');
      setCouponCodeInput(variables.code ?? couponCodeInput);
      setCouponDialogOpen(false);
      invalidatePaymentQueries();
    },
    onError: (error) => errorMsg(error, 'Failed to apply coupon to booking'),
  });

  const removeCouponMutation = useMutation({
    mutationFn: () => {
      if (!bookingSessionId) throw new Error('Missing booking session id');
      return updateBookingSession(bookingSessionId, { coupon_code_id: null });
    },
    onSuccess: () => {
      successMsg('Coupon removed');
      setCouponCodeInput('');
      setCouponDialogOpen(false);
      invalidatePaymentQueries();
    },
    onError: (error) => errorMsg(error, 'Failed to remove coupon'),
  });

  const chargeRemainingBalanceMutation = useMutation({
    mutationFn: () => {
      if (!bookingSessionId) throw new Error('Missing booking session id');
      return chargeBookingSessionBalance(bookingSessionId);
    },
    onSuccess: () => {
      successMsg('Remaining balance charged');
      invalidatePaymentQueries();
    },
    onError: (error) => errorMsg(error, 'Failed to charge remaining balance'),
  });

  const holdOutstandingBalanceMutation = useMutation({
    mutationFn: () => {
      if (!bookingSessionId) throw new Error('Missing booking session id');
      return holdBookingSessionOutstandingBalance(bookingSessionId);
    },
    onSuccess: () => {
      successMsg('Outstanding balance hold placed');
      setPaymentHoldDialogOpen(false);
      invalidatePaymentQueries();
    },
    onError: (error) => errorMsg(error, 'Failed to place payment hold'),
  });

  const chargeCustomAmountMutation = useMutation({
    mutationFn: ({
      amount,
      paymentTypeId,
    }: {
      amount: number;
      paymentTypeId: number;
    }) => {
      if (!bookingSessionId) throw new Error('Missing booking session id');
      return chargeBookingSessionCustomAmount(bookingSessionId, {
        amount,
        payment_type_id: paymentTypeId,
      });
    },
    onSuccess: () => {
      successMsg('Custom amount charged');
      setCustomChargeDialogOpen(false);
      setCustomChargeAmount('');
      setCustomChargePaymentTypeId('');
      invalidatePaymentQueries();
    },
    onError: (error) => errorMsg(error, 'Failed to charge custom amount'),
  });

  const sendPaymentLinkMutation = useMutation({
    mutationFn: ({
      amount,
      paymentTypeId,
      description,
    }: {
      amount: number;
      paymentTypeId: number;
      description?: string;
    }) => {
      if (!bookingSessionId) throw new Error('Missing booking session id');
      return sendBookingSessionPaymentLink(bookingSessionId, {
        amount,
        payment_type_id: paymentTypeId,
        description,
      });
    },
    onSuccess: () => {
      successMsg('Stripe payment link sent');
      setPaymentLinkDialogOpen(false);
      setPaymentLinkAmount('');
      setPaymentLinkDescription('');
      setPaymentLinkPaymentTypeId('');
      invalidatePaymentQueries();
    },
    onError: (error) => errorMsg(error, 'Failed to send payment link'),
  });

  const recordPaymentMutation = useMutation({
    mutationFn: (payload: RecordBookingSessionPaymentPayload) => {
      if (!bookingSessionId) throw new Error('Missing booking session id');
      return recordBookingSessionPayment(bookingSessionId, payload);
    },
    onSuccess: (response) => {
      const updatedSession = mergeRecordedPaymentSession(
        response,
        bookingSession
      );

      if (updatedSession) {
        setLocalBookingSession(updatedSession);
        writeBookingSessionPaymentCache(updatedSession);
      }

      successMsg('Payment recorded');
      setRecordPaymentDialogOpen(false);
      setRecordPaymentAmount('');
      setRecordPaymentPaymentTypeId('');
      setRecordPaymentSource(MANUAL_PAYMENT_DEFAULT_SOURCE);
      setRecordPaymentExternalReference('');
      setRecordPaymentDescription('');
      setRecordPaymentPaidAt('');
      setRecordPaymentSubmitted(false);
      setRecordPaymentFormError('');
      invalidatePaymentQueries();
    },
    onError: (error) => {
      const message = getRecordPaymentErrorMessage(error);
      setRecordPaymentFormError(message);
      errorMsg(error, message);
    },
  });

  const voidPaymentMutation = useMutation({
    mutationFn: ({
      transactionId,
      payload,
    }: {
      transactionId: number;
      payload: VoidBookingSessionPaymentPayload;
    }) => {
      if (!bookingSessionId) throw new Error('Missing booking session id');
      return voidBookingSessionPayment(
        bookingSessionId,
        transactionId,
        payload
      );
    },
    onSuccess: (response) => {
      const updatedSession = mergeRecordedPaymentSession(
        response,
        bookingSession
      );

      if (updatedSession) {
        setLocalBookingSession(updatedSession);
        writeBookingSessionPaymentCache(updatedSession);
      }

      successMsg('Payment voided');
      setVoidPaymentDialogOpen(false);
      setSelectedVoidTransactionId(null);
      setVoidPaymentType('full');
      setVoidPaymentAmount('');
      setVoidPaymentReason('');
      setVoidPaymentDescription('');
      setVoidPaymentSubmitted(false);
      setVoidPaymentFormError('');
      invalidatePaymentQueries();
    },
    onError: (error) => {
      const message = getVoidPaymentErrorMessage(error);
      setVoidPaymentFormError(message);
      errorMsg(error, message);
    },
  });

  const refundPaymentMutation = useMutation({
    mutationFn: ({
      transactionId,
      amount,
      reason,
      description,
    }: {
      transactionId: number;
      amount: number;
      reason: string;
      description?: string;
    }) => {
      if (!bookingSessionId) throw new Error('Missing booking session id');
      return refundBookingSessionPayment(bookingSessionId, {
        transaction_id: transactionId,
        amount,
        reason,
        description,
      });
    },
    onSuccess: () => {
      successMsg('Refund processed');
      setRefundDialogOpen(false);
      setSelectedRefundTransactionId(null);
      setRefundAmount('');
      setRefundReason(REFUND_REASON_OPTIONS[0].value);
      setRefundDescription('');
      invalidatePaymentQueries();
    },
    onError: (error) => errorMsg(error, 'Failed to process refund'),
  });

  const previewRepriceMutation = useMutation({
    mutationFn: ({
      pricePerHour,
      applyToAllFuture,
      reason,
    }: {
      pricePerHour: number;
      applyToAllFuture: boolean;
      reason?: string;
    }) => {
      if (!bookingSessionId) throw new Error('Missing booking session id');
      return repriceBookingSession(bookingSessionId, {
        price_per_hour: pricePerHour,
        apply_to_all_future: applyToAllFuture,
        preview: true,
        reason,
      });
    },
    onSuccess: (response, variables) => {
      setRepricePreviewRows(
        normalizeRepricePreviewResponse(response, variables.pricePerHour)
      );
    },
    onError: (error) => errorMsg(error, 'Failed to preview price changes'),
  });

  const applyRepriceMutation = useMutation({
    mutationFn: ({
      pricePerHour,
      applyToAllFuture,
      reason,
    }: {
      pricePerHour: number;
      applyToAllFuture: boolean;
      reason?: string;
    }) => {
      if (!bookingSessionId) throw new Error('Missing booking session id');
      return repriceBookingSession(bookingSessionId, {
        price_per_hour: pricePerHour,
        apply_to_all_future: applyToAllFuture,
        preview: false,
        reason,
      });
    },
    onSuccess: (response) => {
      const updatedSessions = getRepricedSessions(response);
      const selectedUpdatedSession = updatedSessions.find(
        (session) => session.id === bookingSessionId
      );

      updatedSessions.forEach((updatedSession) => {
        writeBookingSessionPaymentCache(updatedSession);
      });

      if (selectedUpdatedSession) {
        setLocalBookingSession(selectedUpdatedSession);
      }

      const updatedSessionsCount = getRepricedSessionsCount(response);
      successMsg(
        updatedSessionsCount > 0
          ? `Updated ${updatedSessionsCount} session${
              updatedSessionsCount === 1 ? '' : 's'
            }`
          : 'Session pricing updated'
      );
      setRepriceDialogOpen(false);
      setRepriceConfirmDialogOpen(false);
      setRepricePreviewRows(null);
      setRepriceReason('');
      setRepriceApplyToAllFuture(false);
      invalidatePaymentQueries();
    },
    onError: (error) => errorMsg(error, 'Failed to apply new price'),
  });

  const verifyCouponMutation = useMutation({
    mutationFn: (code: string) => {
      if (!serviceVariantId) throw new Error('Missing service variant id');
      return verifyCoupon({
        service_variant_id: serviceVariantId,
        couponCode: code,
        platform: 'ADMIN_DASHBOARD',
      });
    },
  });

  const couponActionLoading =
    applySessionCouponMutation.isLoading ||
    applyBookingCouponMutation.isLoading ||
    removeCouponMutation.isLoading ||
    verifyCouponMutation.isLoading;

  const isApplyCouponLoading = applyToAllFutureSessions
    ? applyBookingCouponMutation.isLoading
    : applySessionCouponMutation.isLoading;

  const updatePaymentMethodMutation = useMutation({
    mutationFn: (walletId: number) => {
      if (!bookingId) throw new Error('Missing booking id');
      return updateBookingPaymentMethod(bookingId, walletId);
    },
    onSuccess: (_, walletId) => {
      const numericWalletId =
        typeof walletId === 'number' ? walletId : Number(walletId);

      if (!Number.isNaN(numericWalletId)) {
        setSavedWalletId(numericWalletId);
        pendingWalletIdRef.current = numericWalletId;

        const matchedWallet = findWalletByIdentifiers(mergedWallets, {
          id: numericWalletId,
        });
        if (matchedWallet?.stripe_id) {
          setSavedStripeId(matchedWallet.stripe_id);
        }
      }

      successMsg('Payment method updated');
      invalidatePaymentQueries();
      closeEdit();
    },
    onError: (error) => errorMsg(error, 'Failed to update payment method'),
  });

  const sendPaymentMethodRequestMutation = useMutation({
    mutationFn: () => {
      if (!bookingId) throw new Error('Missing booking id');
      return sendBookingPaymentMethodRequest(bookingId);
    },
    onSuccess: () => {
      successMsg('Payment method email sent');
      setPaymentMethodRequestDialogOpen(false);
      invalidatePaymentQueries();
    },
    onError: (error) => errorMsg(error, 'Failed to send payment method email'),
  });

  const handleSave = () => {
    if (!selectedWalletId) return;
    updatePaymentMethodMutation.mutate(selectedWalletId);
  };

  const handleClosePaymentMethodRequestDialog = () => {
    if (sendPaymentMethodRequestMutation.isLoading) return;
    setPaymentMethodRequestDialogOpen(false);
  };

  const handleSendPaymentMethodRequest = () => {
    sendPaymentMethodRequestMutation.mutate();
  };

  const handleOpenCouponDialog = () => {
    setCouponCodeInput(activeCouponCode);
    setApplyToAllFutureSessions(false);
    setCouponDialogOpen(true);
  };

  const handleCloseCouponDialog = () => {
    if (couponActionLoading) return;
    setCouponDialogOpen(false);
    setApplyToAllFutureSessions(false);
  };

  const trimmedCouponInput = couponCodeInput.trim();
  const isApplyDisabled =
    !bookingSessionId ||
    !serviceVariantId ||
    !trimmedCouponInput ||
    (hasCouponApplied &&
      trimmedCouponInput.toLowerCase() === activeCouponCode.toLowerCase());

  const couponHelperText = (() => {
    if (
      trimmedCouponInput &&
      hasCouponApplied &&
      trimmedCouponInput.toLowerCase() === activeCouponCode.toLowerCase()
    ) {
      return 'Coupon already applied';
    }

    if (hasCouponApplied) {
      return `Current coupon: ${activeCouponCode}`;
    }

    return 'Enter a coupon code to apply a discount.';
  })();

  const applyButtonLabel = hasCouponApplied ? 'Update coupon' : 'Apply coupon';

  const canApplyToAllSessions = Boolean(bookingId);

  const handleApplyCoupon = async () => {
    if (isApplyDisabled || couponActionLoading) return;

    const normalizedInput = trimmedCouponInput.toLowerCase();
    const matchedCoupon = adminCoupons.find(
      (coupon) => coupon.code.toLowerCase() === normalizedInput
    );

    try {
      if (applyToAllFutureSessions && !canApplyToAllSessions) {
        errorMsg(
          'Cannot apply coupon to future sessions without a booking id.'
        );
        return;
      }

      let couponId: number | null = matchedCoupon?.id ?? null;
      let couponCodeValue = matchedCoupon?.code ?? trimmedCouponInput;

      if (matchedCoupon) {
        setCouponCodeInput(matchedCoupon.code ?? trimmedCouponInput);
      } else {
        const verifiedCoupon = await verifyCouponMutation.mutateAsync(
          trimmedCouponInput
        );
        couponId = verifiedCoupon.id;
        couponCodeValue = verifiedCoupon.code ?? trimmedCouponInput;
        setCouponCodeInput(couponCodeValue);
      }

      if (!couponId) throw new Error('Invalid coupon response');

      if (applyToAllFutureSessions) {
        applyBookingCouponMutation.mutate({ code: couponCodeValue });
      } else {
        applySessionCouponMutation.mutate({ couponId });
      }
    } catch (error) {
      errorMsg(error, 'Failed to validate coupon');
    }
  };

  const handleRemoveCoupon = () => {
    if (!hasCouponApplied || couponActionLoading) return;
    removeCouponMutation.mutate();
  };

  const canChargeRemainingBalance = Boolean(
    bookingSessionId && hasOutstandingBalance && hasPaymentMethodForCharge
  );
  const chargeButtonDisabled =
    !canChargeRemainingBalance || chargeRemainingBalanceMutation.isLoading;
  const canHoldOutstandingBalance = canChargeRemainingBalance;
  const holdButtonDisabled =
    !canHoldOutstandingBalance || holdOutstandingBalanceMutation.isLoading;
  const chargeButtonHelperText = (() => {
    if (!hasOutstandingBalance) {
      return 'No outstanding balance to charge.';
    }

    if (!hasPaymentMethodForCharge) {
      return 'Set a payment method before charging the remaining balance.';
    }

    if (!bookingSessionId) {
      return 'Booking session reference unavailable for charging.';
    }

    if (chargeMethodLabel) {
      return `Charge will use ${chargeMethodLabel}.`;
    }

    return '';
  })();

  const canChargeCustomAmount = Boolean(
    bookingSessionId && hasPaymentMethodForCharge
  );
  const customChargeButtonDisabled = !canChargeCustomAmount;

  const trimmedCustomChargeAmount = customChargeAmount.trim();
  const parsedCustomChargeAmount = Number(trimmedCustomChargeAmount);
  const isCustomChargeAmountValid =
    trimmedCustomChargeAmount.length > 0 &&
    Number.isFinite(parsedCustomChargeAmount) &&
    parsedCustomChargeAmount > 0;
  const customChargeTypeIdValue =
    typeof customChargePaymentTypeId === 'number'
      ? customChargePaymentTypeId
      : Number(customChargePaymentTypeId);
  const isCustomChargeTypeValid =
    Number.isFinite(customChargeTypeIdValue) && customChargeTypeIdValue > 0;
  const customChargeAmountError =
    trimmedCustomChargeAmount.length > 0 && !isCustomChargeAmountValid;
  const customChargeTypeError =
    customChargePaymentTypeId !== '' && !isCustomChargeTypeValid;
  const canSubmitCustomCharge =
    canChargeCustomAmount &&
    isCustomChargeAmountValid &&
    isCustomChargeTypeValid;

  const trimmedRefundAmount = refundAmount.trim();
  const trimmedRefundDescription = refundDescription.trim();
  const parsedRefundAmount = Number(trimmedRefundAmount);
  const isRefundAmountValid =
    trimmedRefundAmount.length > 0 &&
    Number.isFinite(parsedRefundAmount) &&
    parsedRefundAmount > 0;
  const refundAmountError =
    trimmedRefundAmount.length > 0 &&
    (!isRefundAmountValid ||
      parsedRefundAmount > selectedRefundRemainingAmount);
  const refundReasonError = !refundReason.trim();
  const canSubmitRefund = Boolean(
    bookingSessionId &&
      selectedRefundTransaction &&
      canRefundTransaction(selectedRefundTransaction, transactions) &&
      isRefundAmountValid &&
      !refundAmountError &&
      !refundReasonError
  );
  const refundDialogHelperText = (() => {
    if (!selectedRefundTransaction) {
      return 'Select a refundable Stripe transaction first.';
    }

    if (!getTransactionPaymentIntent(selectedRefundTransaction)) {
      return 'Only Stripe-backed transactions can be refunded from the platform.';
    }

    if (!canRefundTransaction(selectedRefundTransaction, transactions)) {
      return 'This transaction is not refundable.';
    }

    return `Up to ${formatTransactionAmount(
      selectedRefundRemainingAmount,
      selectedRefundCurrency
    )} can be refunded for this payment.`;
  })();

  const trimmedVoidPaymentAmount = voidPaymentAmount.trim();
  const trimmedVoidPaymentReason = voidPaymentReason.trim();
  const trimmedVoidPaymentDescription = voidPaymentDescription.trim();
  const parsedVoidPaymentAmount = Number(trimmedVoidPaymentAmount);
  const isPartialVoid = voidPaymentType === 'partial';
  const isVoidPaymentAmountValid =
    !isPartialVoid ||
    (trimmedVoidPaymentAmount.length > 0 &&
      Number.isFinite(parsedVoidPaymentAmount) &&
      parsedVoidPaymentAmount > 0);
  const isVoidPaymentOverRemaining =
    isPartialVoid &&
    isVoidPaymentAmountValid &&
    parsedVoidPaymentAmount > selectedVoidRemainingAmount;
  const voidPaymentAmountError =
    isPartialVoid &&
    trimmedVoidPaymentAmount.length > 0 &&
    (!isVoidPaymentAmountValid || isVoidPaymentOverRemaining);
  const voidPaymentReasonError =
    voidPaymentSubmitted && trimmedVoidPaymentReason.length === 0;
  const canAttemptVoidPayment = Boolean(
    bookingSessionId &&
      selectedVoidTransaction &&
      canVoidTransaction(selectedVoidTransaction, transactions) &&
      isVoidPaymentAmountValid &&
      !isVoidPaymentOverRemaining &&
      !voidPaymentMutation.isLoading
  );
  const canSubmitVoidPayment = Boolean(
    canAttemptVoidPayment && trimmedVoidPaymentReason
  );
  const voidPaymentAmountHelperText = (() => {
    if (!isPartialVoid) {
      return 'Full voids reverse the remaining voidable amount.';
    }

    if (voidPaymentAmountError) {
      if (!isVoidPaymentAmountValid) {
        return 'Enter a valid amount greater than zero.';
      }

      return 'Void amount cannot exceed the remaining voidable balance.';
    }

    return `Up to ${formatTransactionAmount(
      selectedVoidRemainingAmount,
      selectedVoidCurrency
    )} can be voided for this payment.`;
  })();

  const trimmedPaymentLinkAmount = paymentLinkAmount.trim();
  const trimmedPaymentLinkDescription = paymentLinkDescription.trim();
  const parsedPaymentLinkAmount = Number(trimmedPaymentLinkAmount);
  const isPaymentLinkAmountValid =
    trimmedPaymentLinkAmount.length > 0 &&
    Number.isFinite(parsedPaymentLinkAmount) &&
    parsedPaymentLinkAmount > 0;
  const paymentLinkTypeIdValue =
    typeof paymentLinkPaymentTypeId === 'number'
      ? paymentLinkPaymentTypeId
      : Number(paymentLinkPaymentTypeId);
  const isPaymentLinkTypeValid =
    Number.isFinite(paymentLinkTypeIdValue) && paymentLinkTypeIdValue > 0;
  const paymentLinkAmountError =
    trimmedPaymentLinkAmount.length > 0 && !isPaymentLinkAmountValid;
  const paymentLinkTypeError =
    paymentLinkPaymentTypeId !== '' && !isPaymentLinkTypeValid;
  const canSendPaymentLink = Boolean(bookingSessionId);
  const paymentLinkButtonDisabled =
    !canSendPaymentLink || sendPaymentLinkMutation.isLoading;
  const canSubmitPaymentLink =
    canSendPaymentLink && isPaymentLinkAmountValid && isPaymentLinkTypeValid;
  const hasSessionPaymentHoldStatus =
    isHoldStatusValue(bookingSession?.booking_session_payment_status?.value) ||
    isHoldStatusValue(bookingSession?.booking_session_payment_status?.display);
  const hasActiveHoldTransactions = transactions.some(isActiveHoldTransaction);
  const recordPaymentBlockedByHold =
    hasSessionPaymentHoldStatus || hasActiveHoldTransactions;
  const canOpenRecordPaymentDialog = Boolean(
    bookingSessionId && hasOutstandingBalance && !recordPaymentBlockedByHold
  );
  const recordPaymentButtonDisabled =
    !canOpenRecordPaymentDialog || recordPaymentMutation.isLoading;
  const recordPaymentButtonHelperText = (() => {
    if (recordPaymentBlockedByHold) {
      return 'A payment hold is active for this session.';
    }

    if (!hasOutstandingBalance) {
      return 'No outstanding balance to record.';
    }

    if (!bookingSessionId) {
      return 'Booking session reference unavailable for recording payment.';
    }

    return 'Record a manual payment';
  })();
  const trimmedRecordPaymentAmount = recordPaymentAmount.trim();
  const trimmedRecordPaymentSource = recordPaymentSource.trim();
  const trimmedRecordPaymentExternalReference =
    recordPaymentExternalReference.trim();
  const trimmedRecordPaymentDescription = recordPaymentDescription.trim();
  const parsedRecordPaymentAmount = Number(trimmedRecordPaymentAmount);
  const isRecordPaymentAmountPositive =
    trimmedRecordPaymentAmount.length > 0 &&
    Number.isFinite(parsedRecordPaymentAmount) &&
    parsedRecordPaymentAmount > 0;
  const isRecordPaymentOverOutstanding =
    isRecordPaymentAmountPositive &&
    parsedRecordPaymentAmount > outstandingValue;
  const recordPaymentAmountError =
    trimmedRecordPaymentAmount.length > 0 &&
    (!isRecordPaymentAmountPositive || isRecordPaymentOverOutstanding);
  const recordPaymentAmountHelperText = (() => {
    if (!recordPaymentAmountError) {
      return `Up to ${toCurrency(outstandingValue)} can be recorded.`;
    }

    if (!isRecordPaymentAmountPositive) {
      return 'Enter a valid amount greater than zero.';
    }

    return 'Payment amount cannot exceed the outstanding balance.';
  })();
  const recordPaymentTypeIdValue =
    typeof recordPaymentPaymentTypeId === 'number'
      ? recordPaymentPaymentTypeId
      : Number(recordPaymentPaymentTypeId);
  const isRecordPaymentTypeValid =
    Number.isFinite(recordPaymentTypeIdValue) && recordPaymentTypeIdValue > 0;
  const recordPaymentTypeError =
    recordPaymentSubmitted && !isRecordPaymentTypeValid;
  const recordPaymentPaidAtIso =
    getIsoDateTimeFromInputValue(recordPaymentPaidAt);
  const canSubmitRecordPayment =
    canOpenRecordPaymentDialog &&
    isRecordPaymentAmountPositive &&
    !isRecordPaymentOverOutstanding &&
    isRecordPaymentTypeValid &&
    !recordPaymentMutation.isLoading;
  const trimmedRepriceRate = repriceRateInput.trim();
  const trimmedRepriceReason = repriceReason.trim();
  const parsedRepriceRate = Number(trimmedRepriceRate);
  const isRepriceRateValid =
    trimmedRepriceRate.length > 0 &&
    Number.isFinite(parsedRepriceRate) &&
    parsedRepriceRate >= 0;
  const repriceRateError = trimmedRepriceRate.length > 0 && !isRepriceRateValid;
  const hasPreviewResults = Boolean(repricePreviewRows);
  const eligibleRepriceRows = repricePreviewRows?.eligible ?? [];
  const skippedRepriceRows = repricePreviewRows?.skipped ?? [];
  const canOpenRepriceDialog = Boolean(bookingSessionId);
  const canPreviewReprice =
    canOpenRepriceDialog &&
    isRepriceRateValid &&
    !previewRepriceMutation.isLoading &&
    !applyRepriceMutation.isLoading;
  const canApplyReprice =
    canOpenRepriceDialog &&
    isRepriceRateValid &&
    eligibleRepriceRows.length > 0 &&
    hasPreviewResults &&
    !applyRepriceMutation.isLoading;
  const actionHelperTexts = [
    chargeButtonHelperText,
    // customChargeButtonHelperText,
    // paymentLinkButtonHelperText,
  ].filter((text): text is string => Boolean(text));
  const deleteTransactionAmountLabel = transactionToDelete
    ? formatTransactionAmount(
        getTransactionSignedAmount(transactionToDelete),
        transactionToDelete.currency
      )
    : '—';

  const handleChargeRemainingBalance = () => {
    if (!canChargeRemainingBalance) return;
    chargeRemainingBalanceMutation.mutate();
  };

  const handleOpenPaymentHoldDialog = () => {
    if (!canHoldOutstandingBalance) return;
    setPaymentHoldDialogOpen(true);
  };

  const handleClosePaymentHoldDialog = () => {
    if (holdOutstandingBalanceMutation.isLoading) return;
    setPaymentHoldDialogOpen(false);
  };

  const handleHoldOutstandingBalance = () => {
    if (!canHoldOutstandingBalance) return;
    holdOutstandingBalanceMutation.mutate();
  };

  const handleOpenCustomChargeDialog = () => {
    if (!canChargeCustomAmount) return;
    setCustomChargeAmount('');
    setCustomChargePaymentTypeId('');
    setCustomChargeDialogOpen(true);
  };

  const handleCloseCustomChargeDialog = () => {
    if (chargeCustomAmountMutation.isLoading) return;
    setCustomChargeDialogOpen(false);
  };

  const handleSubmitCustomCharge = () => {
    if (!canSubmitCustomCharge) return;
    chargeCustomAmountMutation.mutate({
      amount: parsedCustomChargeAmount,
      paymentTypeId: customChargeTypeIdValue,
    });
  };

  const handleSubmitRefund = () => {
    if (!canSubmitRefund || !selectedRefundTransaction) return;

    refundPaymentMutation.mutate({
      transactionId: selectedRefundTransaction.id,
      amount: parsedRefundAmount,
      reason: refundReason,
      description: trimmedRefundDescription || undefined,
    });
  };

  const handleSubmitVoidPayment = () => {
    setVoidPaymentSubmitted(true);
    setVoidPaymentFormError('');

    if (!canSubmitVoidPayment || !selectedVoidTransaction) return;

    const payload: VoidBookingSessionPaymentPayload = {
      reason: trimmedVoidPaymentReason,
    };

    if (isPartialVoid) {
      payload.amount = parsedVoidPaymentAmount;
    }

    if (trimmedVoidPaymentDescription) {
      payload.description = trimmedVoidPaymentDescription;
    }

    voidPaymentMutation.mutate({
      transactionId: selectedVoidTransaction.id,
      payload,
    });
  };

  const handleOpenPaymentLinkDialog = () => {
    if (!canSendPaymentLink) return;
    setPaymentLinkAmount(
      hasOutstandingBalance && outstandingValue > 0
        ? String(outstandingValue)
        : ''
    );
    setPaymentLinkDescription('');
    setPaymentLinkPaymentTypeId('');
    setPaymentLinkDialogOpen(true);
  };

  const handleClosePaymentLinkDialog = () => {
    if (sendPaymentLinkMutation.isLoading) return;
    setPaymentLinkDialogOpen(false);
  };

  const handleSubmitPaymentLink = () => {
    if (!canSubmitPaymentLink) return;
    sendPaymentLinkMutation.mutate({
      amount: parsedPaymentLinkAmount,
      description: trimmedPaymentLinkDescription || undefined,
      paymentTypeId: paymentLinkTypeIdValue,
    });
  };

  const handleOpenRecordPaymentDialog = () => {
    if (!canOpenRecordPaymentDialog) return;

    setRecordPaymentAmount(String(outstandingValue));
    setRecordPaymentPaymentTypeId('');
    setRecordPaymentSource(MANUAL_PAYMENT_DEFAULT_SOURCE);
    setRecordPaymentExternalReference('');
    setRecordPaymentDescription('');
    setRecordPaymentPaidAt(getLocalDateTimeInputValue());
    setRecordPaymentSubmitted(false);
    setRecordPaymentFormError('');
    setRecordPaymentDialogOpen(true);
  };

  const handleCloseRecordPaymentDialog = () => {
    if (recordPaymentMutation.isLoading) return;
    setRecordPaymentDialogOpen(false);
    setRecordPaymentSubmitted(false);
    setRecordPaymentFormError('');
  };

  const handleSubmitRecordPayment = () => {
    setRecordPaymentSubmitted(true);
    setRecordPaymentFormError('');

    if (!canSubmitRecordPayment) return;

    recordPaymentMutation.mutate({
      amount: parsedRecordPaymentAmount,
      payment_type_id: recordPaymentTypeIdValue,
      source: trimmedRecordPaymentSource || undefined,
      external_reference: trimmedRecordPaymentExternalReference || undefined,
      description: trimmedRecordPaymentDescription || undefined,
      paid_at: recordPaymentPaidAtIso,
    });
  };

  const handleOpenRepriceDialog = () => {
    if (!canOpenRepriceDialog) return;
    setRepriceRateInput(
      isRawPricePerHourMissing ? '' : String(Number(pricePerHour.toFixed(2)))
    );
    setRepriceReason('');
    setRepriceApplyToAllFuture(false);
    setRepricePreviewRows(null);
    setRepriceConfirmDialogOpen(false);
    setRepriceDialogOpen(true);
  };

  const handleCloseRepriceDialog = () => {
    if (previewRepriceMutation.isLoading || applyRepriceMutation.isLoading) {
      return;
    }

    setRepriceDialogOpen(false);
    setRepriceConfirmDialogOpen(false);
    setRepricePreviewRows(null);
  };

  const handlePreviewReprice = () => {
    if (!canPreviewReprice) return;

    previewRepriceMutation.mutate({
      pricePerHour: parsedRepriceRate,
      applyToAllFuture: repriceApplyToAllFuture,
      reason: trimmedRepriceReason || undefined,
    });
  };

  const handleOpenRepriceConfirmDialog = () => {
    if (!canApplyReprice) return;
    setRepriceConfirmDialogOpen(true);
  };

  const handleCloseRepriceConfirmDialog = () => {
    if (applyRepriceMutation.isLoading) return;
    setRepriceConfirmDialogOpen(false);
  };

  const handleApplyReprice = () => {
    if (!canApplyReprice) return;

    applyRepriceMutation.mutate({
      pricePerHour: parsedRepriceRate,
      applyToAllFuture: repriceApplyToAllFuture,
      reason: trimmedRepriceReason || undefined,
    });
  };

  return (
    <VBMCardWrapper>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1, gap: 1 }}
      >
        <VBMCardTitle title="Payment Details" />
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <Tooltip title={recordPaymentButtonHelperText}>
            <span>
              <Button
                size="small"
                variant="outlined"
                aria-label="Record payment"
                onClick={handleOpenRecordPaymentDialog}
                disabled={recordPaymentButtonDisabled}
                sx={{
                  minWidth: 36,
                  px: 1,
                  textTransform: 'none',
                }}
              >
                <Iconify icon="material-symbols:add" width={18} />
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            onClick={handleOpenTransactionsDialog}
            disabled={!hasTransactions}
            startIcon={<Iconify icon="material-symbols:history" width={18} />}
            sx={{ textTransform: 'none' }}
          >
            Transactions history
          </Button>
        </Stack>
      </Stack>

      <Stack
        spacing={1}
        divider={<Divider orientation="horizontal" flexItem />}
      >
        {transactionDetails.map(({ label, value }) => (
          <Stack key={label} direction="row" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" fontSize={downMd ? 12 : 14}>
                {label}
              </Typography>
              {label === 'Discount' && canManageCoupons && (
                <Button
                  size="small"
                  variant="text"
                  onClick={handleOpenCouponDialog}
                  disabled={couponActionLoading}
                  sx={{ textTransform: 'none' }}
                >
                  {hasCouponApplied ? 'Change coupon' : 'Apply coupon'}
                </Button>
              )}
            </Stack>

            {label === 'Payment Method' ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  minHeight: 32,
                  gap: 1,
                  textAlign: 'right',
                  flexWrap: 'wrap',
                }}
              >
                {/* Current payment method display */}
                {typeof value === 'string' ? (
                  <Typography
                    variant="body2"
                    fontSize={downMd ? 12 : 14}
                    sx={{ textTransform: 'none' }}
                  >
                    {value}
                  </Typography>
                ) : (
                  value
                )}

                {/* Small inline "Change" action */}
                {canEdit && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={(event) => handleOpenEdit(event)}
                    startIcon={
                      <Iconify
                        icon="material-symbols:edit-outline"
                        width={18}
                      />
                    }
                    sx={{ textTransform: 'none' }}
                  >
                    Change
                  </Button>
                )}

                {!hasAttachedPaymentMethod && bookingId && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    onClick={() => setPaymentMethodRequestDialogOpen(true)}
                    startIcon={
                      <Iconify
                        icon="material-symbols:add-card-outline"
                        width={16}
                      />
                    }
                    sx={(theme) => ({
                      minHeight: 34,
                      px: 1.25,
                      borderRadius: 1,
                      borderColor: alpha(theme.palette.success.main, 0.32),
                      bgcolor: alpha(theme.palette.success.main, 0.08),
                      color: theme.palette.success.dark,
                      fontWeight: 700,
                      fontSize: 13,
                      lineHeight: 1.2,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      '& .MuiButton-startIcon': {
                        ml: -0.25,
                        mr: 0.75,
                        color: theme.palette.success.main,
                      },
                      '&:hover': {
                        borderColor: theme.palette.success.main,
                        bgcolor: alpha(theme.palette.success.main, 0.14),
                      },
                    })}
                  >
                    Request payment method
                  </Button>
                )}
              </Box>
            ) : label === 'Price per Hour' ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  minHeight: 32,
                  gap: 1,
                  textAlign: 'right',
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="body2"
                  fontSize={downMd ? 12 : 14}
                  sx={{ textTransform: 'none' }}
                >
                  {value}
                </Typography>
                {canOpenRepriceDialog && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleOpenRepriceDialog}
                    startIcon={
                      <Iconify
                        icon="material-symbols:edit-outline"
                        width={16}
                      />
                    }
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    Change hourly rate
                  </Button>
                )}
              </Box>
            ) : // unchanged for other rows
            typeof value === 'string' || typeof value === 'number' ? (
              <Typography
                variant="body2"
                fontSize={downMd ? 12 : 14}
                sx={{ textTransform: 'none' }}
              >
                {value}
              </Typography>
            ) : (
              value
            )}
          </Stack>
        ))}
      </Stack>

      <Popover
        open={isEditing}
        anchorEl={editAnchorEl}
        onClose={() => {
          if (updatePaymentMethodMutation.isLoading) return;
          closeEdit();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 300,
            maxWidth: 360,
          },
        }}
      >
        <Stack spacing={1.5}>
          <TextField
            select
            size="small"
            value={selectedWalletId ?? ''}
            onChange={(event) => {
              const { value } = event.target;
              setSelectedWalletId(value === '' ? null : Number(value));
            }}
            sx={{ minWidth: 260 }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (
                  selected === undefined ||
                  selected === null ||
                  selected === ''
                ) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Select a payment method
                    </Typography>
                  );
                }
                const walletId = Number(selected);
                const wallet = findWalletByIdentifiers(mergedWallets, {
                  id: Number.isNaN(walletId) ? undefined : walletId,
                });
                return wallet ? (
                  <WalletSummary wallet={wallet} />
                ) : (
                  'Select a payment method'
                );
              },
            }}
          >
            {mergedWallets.map((wallet) => {
              const display = buildWalletDisplay(wallet);
              if (!display) return null;
              return (
                <MenuItem
                  key={wallet.id}
                  value={wallet.id}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 0.25,
                    py: 1.1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={600}>
                      {display.primary}
                    </Typography>
                    {display.isDefault && (
                      <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                      >
                        Default
                      </Typography>
                    )}
                  </Stack>
                  {display.secondary && (
                    <Typography variant="caption" color="text.secondary">
                      {display.secondary}
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </TextField>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              size="small"
              variant="text"
              onClick={closeEdit}
              disabled={updatePaymentMethodMutation.isLoading}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <LoadingButton
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={!selectedWalletId}
              loading={updatePaymentMethodMutation.isLoading}
            >
              Save
            </LoadingButton>
          </Stack>
        </Stack>
      </Popover>

      <Dialog
        fullWidth
        maxWidth="xs"
        open={isPaymentMethodRequestDialogOpen}
        onClose={handleClosePaymentMethodRequestDialog}
      >
        <DialogTitle>Send payment method request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            The client will receive an email with a link to add a payment method
            to this booking.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePaymentMethodRequestDialog}
            disabled={sendPaymentMethodRequestMutation.isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSendPaymentMethodRequest}
            loading={sendPaymentMethodRequestMutation.isLoading}
            disabled={!bookingId}
          >
            Send payment method email
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isTransactionsDialogOpen}
        onClose={handleCloseTransactionsDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Transactions history</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {hasTransactions ? (
            <List disablePadding>
              {transactions.map((transaction, index) => {
                const transactionId = transaction.id ?? index;
                const isExpanded = expandedTransactionId === transactionId;
                const createdAtLabel = transaction.created_at
                  ? fDateTime(transaction.created_at)
                  : '—';
                const updatedAtLabel = transaction.updated_at
                  ? fDateTime(transaction.updated_at)
                  : null;
                const statusPresentation =
                  getTransactionStatusPresentation(transaction);
                const amountLabel = formatTransactionAmount(
                  getTransactionSignedAmount(transaction),
                  transaction.currency
                );
                const typeLabel = getTransactionTypeLabel(transaction);
                const sourceLabel = formatTransactionSource(transaction.source);
                const paymentIntentId =
                  transaction.payment_intent?.trim() || '';
                const paymentIntentValue = paymentIntentId ? (
                  <Link
                    href={buildStripeDashboardPaymentUrl(paymentIntentId)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {paymentIntentId}
                  </Link>
                ) : (
                  '—'
                );
                const paymentTypeLabel =
                  transaction.payment_type?.name?.trim() ||
                  (transaction.payment_type?.id
                    ? `Type #${transaction.payment_type.id}`
                    : '—');
                const walletDisplay = buildWalletDisplay(
                  transaction.wallet as AnyWallet
                );
                const transactionDescription =
                  transaction.description?.trim() || '—';
                const externalReferenceLabel =
                  getTransactionExternalReference(transaction) || '—';
                const paidAtValue = getTransactionPaidAt(transaction);
                const paidAtLabel = paidAtValue ? fDateTime(paidAtValue) : '—';
                const stripeRefundReference =
                  getStripeRefundReference(transaction) || '—';
                const refundReasonLabel = getRefundReason(transaction) || '—';
                const voidReasonLabel = getVoidReason(transaction) || '—';
                const metadataDetailRows =
                  getTransactionMetadataDetailRows(transaction);
                const refundableAmount = getRemainingRefundableAmount(
                  transaction,
                  transactions
                );
                const canRefundThisTransaction = canRefundTransaction(
                  transaction,
                  transactions
                );
                const voidableAmount = getRemainingVoidableAmount(
                  transaction,
                  transactions
                );
                const canVoidThisTransaction = canVoidTransaction(
                  transaction,
                  transactions
                );
                const canDeleteThisTransaction =
                  isSuperAdmin === true && Boolean(transaction.id);

                const detailRows: { label: string; value: ReactNode }[] = [
                  { label: 'Amount', value: amountLabel },
                  { label: 'Source', value: sourceLabel },
                  { label: 'Type', value: typeLabel },
                  { label: 'Payment type', value: paymentTypeLabel },
                  {
                    label: 'External reference',
                    value: externalReferenceLabel,
                  },
                  { label: 'Paid at', value: paidAtLabel },
                  { label: 'Description', value: transactionDescription },
                  {
                    label: 'Payment intent',
                    value: paymentIntentValue,
                  },
                  ...metadataDetailRows,
                  {
                    label: 'Wallet',
                    value: walletDisplay ? (
                      <WalletSummary wallet={transaction.wallet as AnyWallet} />
                    ) : (
                      '—'
                    ),
                  },
                  { label: 'Created', value: createdAtLabel },
                  { label: 'Updated', value: updatedAtLabel || '—' },
                ];

                if (isRefundTransaction(transaction)) {
                  detailRows.splice(5, 0, {
                    label: 'Stripe refund',
                    value: stripeRefundReference,
                  });
                  detailRows.splice(6, 0, {
                    label: 'Refund reason',
                    value: refundReasonLabel,
                  });
                }

                if (isVoidTransaction(transaction)) {
                  detailRows.splice(5, 0, {
                    label: 'Void reason',
                    value: voidReasonLabel,
                  });
                }

                return (
                  <Box key={transactionId}>
                    {index > 0 && <Divider />}
                    <ListItemButton
                      onClick={() => handleToggleTransaction(transactionId)}
                      sx={{
                        alignItems: 'flex-start',
                        py: 1.5,
                        px: { xs: 2, sm: 2.5 },
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 1, sm: 2 }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        sx={{ width: '100%' }}
                      >
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle2">
                            Transaction #{transaction.id ?? '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Created {createdAtLabel}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ ml: { xs: 0, sm: 'auto' } }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {amountLabel}
                          </Typography>
                          <Label color={statusPresentation.color}>
                            {statusPresentation.label}
                          </Label>
                          <Iconify
                            icon="eva:chevron-down-fill"
                            width={18}
                            sx={{
                              transition: 'transform 0.2s ease',
                              transform: isExpanded ? 'rotate(180deg)' : 'none',
                            }}
                          />
                        </Stack>
                      </Stack>
                    </ListItemButton>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Stack
                        spacing={1.5}
                        sx={{ px: { xs: 3, sm: 3.5 }, py: 2 }}
                      >
                        {detailRows.map(({ label, value }) => (
                          <Stack
                            key={label}
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            spacing={2}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {label}
                            </Typography>
                            <Box
                              sx={{
                                textAlign: 'right',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                flex: '1 1 0',
                                minWidth: 0,
                                maxWidth: { xs: '68%', sm: '72%' },
                              }}
                            >
                              {typeof value === 'string' ||
                              typeof value === 'number' ? (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflowWrap: 'anywhere',
                                    whiteSpace: 'normal',
                                  }}
                                >
                                  {value}
                                </Typography>
                              ) : (
                                value
                              )}
                            </Box>
                          </Stack>
                        ))}
                        {canRefundThisTransaction && (
                          <Stack spacing={0.75} alignItems="flex-end">
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Refundable amount:{' '}
                              {formatTransactionAmount(
                                refundableAmount,
                                transaction.currency
                              )}
                            </Typography>
                            <LoadingButton
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                handleOpenRefundDialog(transaction)
                              }
                              loading={
                                refundPaymentMutation.isLoading &&
                                selectedRefundTransactionId === transaction.id
                              }
                              sx={{ textTransform: 'none' }}
                            >
                              Refund payment
                            </LoadingButton>
                          </Stack>
                        )}
                        {canVoidThisTransaction && (
                          <Stack spacing={0.75} alignItems="flex-end">
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Voidable amount:{' '}
                              {formatTransactionAmount(
                                voidableAmount,
                                transaction.currency
                              )}
                            </Typography>
                            <LoadingButton
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() =>
                                handleOpenVoidPaymentDialog(transaction)
                              }
                              loading={
                                voidPaymentMutation.isLoading &&
                                selectedVoidTransactionId === transaction.id
                              }
                              sx={{ textTransform: 'none' }}
                            >
                              Void payment
                            </LoadingButton>
                          </Stack>
                        )}
                        {canDeleteThisTransaction && (
                          <Stack spacing={0.75} alignItems="flex-end">
                            <LoadingButton
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() =>
                                handleOpenDeleteTransactionDialog(transaction)
                              }
                              loading={
                                deleteTransactionMutation.isLoading &&
                                transactionToDelete?.id === transaction.id
                              }
                              sx={{ textTransform: 'none' }}
                            >
                              Delete transaction
                            </LoadingButton>
                          </Stack>
                        )}
                      </Stack>
                    </Collapse>
                  </Box>
                );
              })}
            </List>
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No transactions recorded for this booking session yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransactionsDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(transactionToDelete)}
        onClose={handleCloseDeleteTransactionDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete transaction?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete Transaction #
            {transactionToDelete?.id ?? '—'} for {deleteTransactionAmountLabel}.
            Deleting a transaction can affect payment totals and audit history.
          </DialogContentText>
          {deleteTransactionError && (
            <FormHelperText error sx={{ mt: 2 }}>
              {deleteTransactionError}
            </FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={handleCloseDeleteTransactionDialog}
            disabled={deleteTransactionMutation.isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteTransaction}
            loading={deleteTransactionMutation.isLoading}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Stack spacing={1.5} sx={{ mt: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'flex-start' }}
        >
          <Stack spacing={0.75} flex={1}>
            <LoadingButton
              fullWidth
              size="medium"
              variant="contained"
              color="primary"
              onClick={handleChargeRemainingBalance}
              disabled={chargeButtonDisabled}
              loading={chargeRemainingBalanceMutation.isLoading}
            >
              Charge remaining balance
            </LoadingButton>
          </Stack>

          <Stack spacing={0.75} flex={1}>
            <Button
              fullWidth
              size="medium"
              variant="outlined"
              color="primary"
              onClick={handleOpenCustomChargeDialog}
              disabled={customChargeButtonDisabled}
              sx={{ width: '100%' }}
            >
              Charge custom amount
            </Button>
          </Stack>
        </Stack>
        <Button
          fullWidth
          size="medium"
          variant="contained"
          color="secondary"
          onClick={handleOpenPaymentLinkDialog}
          disabled={paymentLinkButtonDisabled}
        >
          Send payment link to Client
        </Button>
        <LoadingButton
          fullWidth
          size="medium"
          variant="outlined"
          color="warning"
          onClick={handleOpenPaymentHoldDialog}
          disabled={holdButtonDisabled}
          loading={holdOutstandingBalanceMutation.isLoading}
        >
          Place hold on outstanding balance
        </LoadingButton>
        {actionHelperTexts.map((text, index) => (
          <Typography
            key={`${text}-${index}`}
            variant="caption"
            color="text.secondary"
          >
            {text}
          </Typography>
        ))}
      </Stack>

      <Dialog
        open={isRecordPaymentDialogOpen}
        onClose={handleCloseRecordPaymentDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Record payment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Record a manual ledger payment for this session without charging a
              saved card or creating a Stripe payment.
            </Typography>
            <TextField
              label="Amount"
              type="number"
              value={recordPaymentAmount}
              onChange={(event) => {
                setRecordPaymentAmount(event.target.value);
                setRecordPaymentFormError('');
              }}
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              error={recordPaymentAmountError}
              helperText={recordPaymentAmountHelperText}
            />
            <TextField
              select
              label="Payment type"
              value={recordPaymentPaymentTypeId}
              onChange={(event) => {
                const { value } = event.target;
                setRecordPaymentPaymentTypeId(
                  value === '' || value === null ? '' : Number(value)
                );
                setRecordPaymentFormError('');
              }}
              fullWidth
              error={recordPaymentTypeError}
              helperText={
                recordPaymentTypeError
                  ? 'Select a valid payment type.'
                  : 'Required for payment reporting.'
              }
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  const normalizedSelected = Array.isArray(selected)
                    ? selected[0]
                    : selected;

                  const option = CUSTOM_PAYMENT_TYPE_OPTIONS.find(
                    (item) => item.id === Number(normalizedSelected)
                  );

                  return option
                    ? option.label
                    : String(normalizedSelected ?? '');
                },
              }}
            >
              <MenuItem value="">
                <Typography variant="body2" color="text.secondary">
                  Select a payment type
                </Typography>
              </MenuItem>
              {CUSTOM_PAYMENT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Source"
              value={recordPaymentSource}
              onChange={(event) => setRecordPaymentSource(event.target.value)}
              fullWidth
              helperText="Optional source shown in the transaction history."
            />
            <TextField
              label="External reference"
              value={recordPaymentExternalReference}
              onChange={(event) =>
                setRecordPaymentExternalReference(event.target.value)
              }
              fullWidth
              helperText="Optional bank transfer or external payment reference."
            />
            <TextField
              label="Description"
              value={recordPaymentDescription}
              onChange={(event) =>
                setRecordPaymentDescription(event.target.value)
              }
              fullWidth
              multiline
              minRows={2}
              helperText="Optional internal note for this payment."
            />
            <TextField
              label="Paid date/time"
              type="datetime-local"
              value={recordPaymentPaidAt}
              onChange={(event) => setRecordPaymentPaidAt(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Optional. Clear it to let the backend use its default."
            />
            {recordPaymentFormError && (
              <FormHelperText error>{recordPaymentFormError}</FormHelperText>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseRecordPaymentDialog}
            disabled={recordPaymentMutation.isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSubmitRecordPayment}
            disabled={!canSubmitRecordPayment}
            loading={recordPaymentMutation.isLoading}
          >
            Record payment
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isCustomChargeDialogOpen}
        onClose={handleCloseCustomChargeDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Charge custom amount</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Amount to charge"
              type="number"
              value={customChargeAmount}
              onChange={(event) => setCustomChargeAmount(event.target.value)}
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              error={customChargeAmountError}
              helperText={
                customChargeAmountError
                  ? 'Enter a valid amount greater than zero.'
                  : 'Specify the amount that should be charged.'
              }
            />
            <TextField
              select
              label="Payment type"
              value={customChargePaymentTypeId}
              onChange={(event) => {
                const { value } = event.target;
                setCustomChargePaymentTypeId(
                  value === '' || value === null ? '' : Number(value)
                );
              }}
              fullWidth
              error={customChargeTypeError}
              helperText={
                customChargeTypeError
                  ? 'Select a valid payment type.'
                  : 'This helps identify the reason for the charge.'
              }
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  // if (
                  //   selected === '' ||
                  //   selected === undefined ||
                  //   selected === null
                  // ) {
                  //   return (
                  //     <Typography variant="body2" color="text.secondary">
                  //       Select a payment type
                  //     </Typography>
                  //   );
                  // }

                  const normalizedSelected = Array.isArray(selected)
                    ? selected[0]
                    : selected;

                  const option = CUSTOM_PAYMENT_TYPE_OPTIONS.find(
                    (item) => item.id === Number(normalizedSelected)
                  );

                  return option
                    ? option.label
                    : String(normalizedSelected ?? '');
                },
              }}
            >
              <MenuItem value="">
                <Typography variant="body2" color="text.secondary">
                  Select a payment type
                </Typography>
              </MenuItem>
              {CUSTOM_PAYMENT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {chargeMethodLabel && (
              <Typography variant="caption" color="text.secondary">
                Charge will use {chargeMethodLabel}.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseCustomChargeDialog}
            disabled={chargeCustomAmountMutation.isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSubmitCustomCharge}
            disabled={!canSubmitCustomCharge}
            loading={chargeCustomAmountMutation.isLoading}
          >
            Charge amount
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isPaymentHoldDialogOpen}
        onClose={handleClosePaymentHoldDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Place payment hold?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              The outstanding balance of {toCurrency(outstandingValue)} will be
              authorized on the saved payment method, not charged immediately.
            </Typography>
            {chargeMethodLabel && (
              <Typography variant="caption" color="text.secondary">
                Hold will use {chargeMethodLabel}.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePaymentHoldDialog}
            disabled={holdOutstandingBalanceMutation.isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="warning"
            onClick={handleHoldOutstandingBalance}
            disabled={!canHoldOutstandingBalance}
            loading={holdOutstandingBalanceMutation.isLoading}
          >
            Place hold
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRepriceDialogOpen}
        onClose={handleCloseRepriceDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Change hourly rate</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Paid or invoiced sessions cannot be repriced through this flow.
            </Typography>

            <TextField
              label="New hourly rate"
              type="number"
              value={repriceRateInput}
              onChange={(event) => {
                setRepriceRateInput(event.target.value);
                setRepricePreviewRows(null);
              }}
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              error={repriceRateError}
              helperText={
                repriceRateError
                  ? 'Enter a valid hourly rate of zero or more.'
                  : 'This override will be used for the selected session or all eligible future sessions.'
              }
            />

            <TextField
              label="Reason"
              value={repriceReason}
              onChange={(event) => {
                setRepriceReason(event.target.value);
                setRepricePreviewRows(null);
              }}
              fullWidth
              multiline
              minRows={2}
              helperText="Optional internal note, for example Annual pricing update."
            />

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Checkbox
                size="small"
                checked={repriceApplyToAllFuture}
                onChange={(event) => {
                  setRepriceApplyToAllFuture(event.target.checked);
                  setRepricePreviewRows(null);
                }}
              />
              <Stack spacing={0.25}>
                <Typography variant="body2">
                  Apply to all future sessions in this booking
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Preview will show which sessions can be updated and which are
                  blocked.
                </Typography>
              </Stack>
            </Stack>

            {previewRepriceMutation.isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : hasPreviewResults ? (
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  justifyContent="space-between"
                >
                  <Typography variant="subtitle2">Preview results</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Eligible: {eligibleRepriceRows.length} • Skipped:{' '}
                    {skippedRepriceRows.length}
                  </Typography>
                </Stack>

                {eligibleRepriceRows.length > 0 && (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" color="success.main">
                      Eligible sessions
                    </Typography>
                    <Stack
                      divider={<Divider flexItem />}
                      sx={{
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                      }}
                    >
                      {eligibleRepriceRows.map((row) => (
                        <Stack
                          key={`eligible-${row.sessionId}-${row.date}`}
                          direction={{ xs: 'column', md: 'row' }}
                          justifyContent="space-between"
                          spacing={1.5}
                          sx={{ px: 2, py: 1.5 }}
                        >
                          <Stack spacing={0.25}>
                            <Typography variant="body2" fontWeight={600}>
                              Session #{row.sessionId}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {row.date}
                            </Typography>
                          </Stack>
                          <Stack
                            spacing={0.25}
                            sx={{ textAlign: { md: 'right' } }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Hourly rate:{' '}
                              {renderCurrencyOrDash(row.currentHourlyRate)} →{' '}
                              {renderCurrencyOrDash(row.newHourlyRate)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Total: {renderCurrencyOrDash(row.currentTotal)} →{' '}
                              {renderCurrencyOrDash(row.newTotal)}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              Delta:{' '}
                              {row.delta === null
                                ? '—'
                                : toDeltaCurrency(row.delta)}
                            </Typography>
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                )}

                {skippedRepriceRows.length > 0 && (
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" color="warning.main">
                      Skipped sessions
                    </Typography>
                    <Stack
                      divider={<Divider flexItem />}
                      sx={{
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                      }}
                    >
                      {skippedRepriceRows.map((row) => (
                        <Stack
                          key={`skipped-${row.sessionId}-${row.date}-${
                            row.skippedReason ?? 'reason'
                          }`}
                          direction={{ xs: 'column', md: 'row' }}
                          justifyContent="space-between"
                          spacing={1.5}
                          sx={{ px: 2, py: 1.5 }}
                        >
                          <Stack spacing={0.25}>
                            <Typography variant="body2" fontWeight={600}>
                              Session #{row.sessionId}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {row.date}
                            </Typography>
                          </Stack>
                          <Stack
                            spacing={0.25}
                            sx={{ textAlign: { md: 'right' } }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Hourly rate:{' '}
                              {renderCurrencyOrDash(row.currentHourlyRate)} →{' '}
                              {renderCurrencyOrDash(row.newHourlyRate)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Total: {renderCurrencyOrDash(row.currentTotal)} →{' '}
                              {renderCurrencyOrDash(row.newTotal)}
                            </Typography>
                            <Typography variant="body2" color="warning.main">
                              {row.skippedReason ||
                                'Not eligible for repricing'}
                            </Typography>
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                )}

                {!eligibleRepriceRows.length && !skippedRepriceRows.length && (
                  <Typography variant="body2" color="text.secondary">
                    No preview data was returned for this request.
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Preview price changes before applying them.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseRepriceDialog}
            disabled={
              previewRepriceMutation.isLoading || applyRepriceMutation.isLoading
            }
          >
            Cancel
          </Button>
          <LoadingButton
            variant="outlined"
            onClick={handlePreviewReprice}
            disabled={!canPreviewReprice}
            loading={previewRepriceMutation.isLoading}
          >
            Preview price changes
          </LoadingButton>
          <LoadingButton
            variant="contained"
            onClick={handleOpenRepriceConfirmDialog}
            disabled={!canApplyReprice}
            loading={applyRepriceMutation.isLoading}
          >
            Apply new price
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRepriceConfirmDialogOpen}
        onClose={handleCloseRepriceConfirmDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirm repricing</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              This will update {eligibleRepriceRows.length} eligible session
              {eligibleRepriceRows.length === 1 ? '' : 's'} to{' '}
              {isRepriceRateValid
                ? toCurrency(parsedRepriceRate)
                : 'the new rate'}{' '}
              per hour.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {repriceApplyToAllFuture
                ? 'Future sessions in this booking that are eligible will be repriced.'
                : 'Only the selected session will be repriced if it is eligible.'}
            </Typography>
            {skippedRepriceRows.length > 0 && (
              <Typography variant="body2" color="warning.main">
                {skippedRepriceRows.length} session
                {skippedRepriceRows.length === 1 ? '' : 's'} will be skipped.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseRepriceConfirmDialog}
            disabled={applyRepriceMutation.isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleApplyReprice}
            disabled={!canApplyReprice}
            loading={applyRepriceMutation.isLoading}
          >
            Apply new price
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isPaymentLinkDialogOpen}
        onClose={handleClosePaymentLinkDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Send payment link</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              The customer will receive a Stripe payment link to complete the
              payment manually.
            </Typography>
            <TextField
              label="Amount to request"
              type="number"
              value={paymentLinkAmount}
              onChange={(event) => setPaymentLinkAmount(event.target.value)}
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              error={paymentLinkAmountError}
              helperText={
                paymentLinkAmountError
                  ? 'Enter a valid amount greater than zero.'
                  : 'Specify the amount that should be collected via the payment link.'
              }
            />
            <TextField
              label="Description"
              value={paymentLinkDescription}
              onChange={(event) =>
                setPaymentLinkDescription(event.target.value)
              }
              fullWidth
              multiline
              minRows={2}
              helperText="Optional note to explain what this extra payment is for."
            />
            <TextField
              select
              label="Payment type"
              value={paymentLinkPaymentTypeId}
              onChange={(event) => {
                const { value } = event.target;
                setPaymentLinkPaymentTypeId(
                  value === '' || value === null ? '' : Number(value)
                );
              }}
              fullWidth
              error={paymentLinkTypeError}
              helperText={
                paymentLinkTypeError
                  ? 'Select a valid payment type.'
                  : 'This helps identify the reason for the payment request.'
              }
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  const normalizedSelected = Array.isArray(selected)
                    ? selected[0]
                    : selected;

                  const option = CUSTOM_PAYMENT_TYPE_OPTIONS.find(
                    (item) => item.id === Number(normalizedSelected)
                  );

                  return option
                    ? option.label
                    : String(normalizedSelected ?? '');
                },
              }}
            >
              <MenuItem value="">
                <Typography variant="body2" color="text.secondary">
                  Select a payment type
                </Typography>
              </MenuItem>
              {CUSTOM_PAYMENT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePaymentLinkDialog}
            disabled={sendPaymentLinkMutation.isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSubmitPaymentLink}
            disabled={!canSubmitPaymentLink}
            loading={sendPaymentLinkMutation.isLoading}
          >
            Send payment link
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isVoidPaymentDialogOpen}
        onClose={handleCloseVoidPaymentDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Void payment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Void a manually recorded payment by adding a reversal transaction.
            </Typography>
            {selectedVoidTransaction && (
              <Stack spacing={0.5}>
                <Typography variant="body2" fontWeight={600}>
                  Transaction #{selectedVoidTransaction.id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Status: {selectedVoidStatusLabel || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Remaining voidable:{' '}
                  {formatTransactionAmount(
                    selectedVoidRemainingAmount,
                    selectedVoidCurrency
                  )}
                </Typography>
              </Stack>
            )}
            <TextField
              select
              label="Void type"
              value={voidPaymentType}
              onChange={(event) => {
                setVoidPaymentType(event.target.value as VoidPaymentType);
                setVoidPaymentFormError('');
              }}
              fullWidth
              helperText="Choose whether to reverse all or part of this payment."
            >
              {VOID_PAYMENT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {isPartialVoid && (
              <TextField
                label="Amount"
                type="number"
                value={voidPaymentAmount}
                onChange={(event) => {
                  setVoidPaymentAmount(event.target.value);
                  setVoidPaymentFormError('');
                }}
                fullWidth
                inputProps={{ min: 0, step: '0.01' }}
                error={voidPaymentAmountError}
                helperText={voidPaymentAmountHelperText}
              />
            )}
            <TextField
              label="Reason"
              value={voidPaymentReason}
              onChange={(event) => {
                setVoidPaymentReason(event.target.value);
                setVoidPaymentFormError('');
              }}
              fullWidth
              multiline
              minRows={2}
              error={voidPaymentReasonError}
              helperText={
                voidPaymentReasonError
                  ? 'Enter a reason for voiding this payment.'
                  : 'Required for audit history.'
              }
            />
            <TextField
              label="Description"
              value={voidPaymentDescription}
              onChange={(event) =>
                setVoidPaymentDescription(event.target.value)
              }
              fullWidth
              multiline
              minRows={2}
              helperText="Optional internal note for the void."
            />
            {voidPaymentFormError && (
              <FormHelperText error>{voidPaymentFormError}</FormHelperText>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseVoidPaymentDialog}
            disabled={voidPaymentMutation.isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="warning"
            onClick={handleSubmitVoidPayment}
            disabled={!canAttemptVoidPayment}
            loading={voidPaymentMutation.isLoading}
          >
            Void payment
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRefundDialogOpen}
        onClose={() => {
          if (refundPaymentMutation.isLoading) return;
          handleCloseRefundDialog();
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Refund payment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Refunds are processed in Stripe and recorded back into the
              platform transaction history.
            </Typography>
            {selectedRefundTransaction && (
              <Stack spacing={0.5}>
                <Typography variant="body2" fontWeight={600}>
                  Transaction #{selectedRefundTransaction.id}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Status: {selectedRefundStatusLabel || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Payment intent:{' '}
                  {getTransactionPaymentIntent(selectedRefundTransaction) ||
                    '—'}
                </Typography>
              </Stack>
            )}
            <TextField
              label="Refund amount"
              type="number"
              value={refundAmount}
              onChange={(event) => setRefundAmount(event.target.value)}
              fullWidth
              inputProps={{ min: 0, step: '0.01' }}
              error={refundAmountError}
              helperText={
                refundAmountError
                  ? `Enter an amount up to ${formatTransactionAmount(
                      selectedRefundRemainingAmount,
                      selectedRefundCurrency
                    )}.`
                  : refundDialogHelperText
              }
            />
            <TextField
              select
              label="Reason"
              value={refundReason}
              onChange={(event) => setRefundReason(event.target.value)}
              fullWidth
              error={refundReasonError}
              helperText={
                refundReasonError
                  ? 'Select a refund reason.'
                  : 'This reason will be sent with the refund request.'
              }
            >
              {REFUND_REASON_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              value={refundDescription}
              onChange={(event) => setRefundDescription(event.target.value)}
              fullWidth
              multiline
              minRows={2}
              helperText="Optional internal note for the refund."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseRefundDialog}
            disabled={refundPaymentMutation.isLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={handleSubmitRefund}
            disabled={!canSubmitRefund}
            loading={refundPaymentMutation.isLoading}
          >
            Refund payment
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {canManageCoupons && (
        <Dialog
          open={isCouponDialogOpen}
          onClose={handleCloseCouponDialog}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>
            {hasCouponApplied ? 'Change coupon' : 'Apply coupon'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Coupon code"
              fullWidth
              value={couponCodeInput}
              onChange={(event) => setCouponCodeInput(event.target.value)}
              disabled={couponActionLoading}
              placeholder="Enter coupon"
            />
            <FormHelperText>{couponHelperText}</FormHelperText>

            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              sx={{ mt: 1 }}
            >
              <Checkbox
                size="small"
                checked={applyToAllFutureSessions}
                onChange={(event) =>
                  setApplyToAllFutureSessions(event.target.checked)
                }
                disabled={!canApplyToAllSessions || couponActionLoading}
              />
              <Stack spacing={0.25}>
                <Typography
                  variant="body2"
                  color={
                    !canApplyToAllSessions ? 'text.disabled' : 'text.primary'
                  }
                >
                  Apply to all future sessions
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {canApplyToAllSessions
                    ? 'Discount will be applied to the remaining sessions in this booking.'
                    : 'Available once a booking has been created.'}
                </Typography>
              </Stack>
            </Stack>

            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Available coupons (Admin Dashboard)
              </Typography>

              {isAdminCouponsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : adminCoupons.length ? (
                <List
                  dense
                  sx={{
                    maxHeight: 220,
                    overflowY: 'auto',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  {adminCoupons.map((coupon) => {
                    const numericCouponValue = Number(coupon.value);
                    const formattedValue =
                      coupon.value_type === 'PERCENTAGE'
                        ? `${coupon.value}%`
                        : Number.isFinite(numericCouponValue)
                        ? toCurrency(numericCouponValue)
                        : String(coupon.value);
                    const primaryText = `${coupon.code} — ${formattedValue}`;
                    const secondaryText =
                      coupon.description?.trim() || undefined;
                    const isSelected =
                      trimmedCouponInput.toLowerCase() ===
                      coupon.code.toLowerCase();
                    const isUserSpecific = Boolean(coupon.user_id);

                    return (
                      <ListItemButton
                        key={coupon.id}
                        selected={isSelected}
                        onClick={() =>
                          !couponActionLoading &&
                          setCouponCodeInput(coupon.code ?? '')
                        }
                        disabled={couponActionLoading}
                      >
                        <ListItemText
                          primary={primaryText}
                          secondary={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              {secondaryText && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {secondaryText}
                                </Typography>
                              )}
                              <Chip
                                label={
                                  isUserSpecific ? 'User specific' : 'System'
                                }
                                size="small"
                                color={isUserSpecific ? 'primary' : 'default'}
                                variant={isUserSpecific ? 'filled' : 'outlined'}
                              />
                            </Stack>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No active coupons available for the Admin Dashboard.
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseCouponDialog}
              disabled={couponActionLoading}
            >
              Cancel
            </Button>
            {hasCouponApplied && (
              <Button
                color="error"
                onClick={handleRemoveCoupon}
                disabled={couponActionLoading}
              >
                Remove coupon
              </Button>
            )}
            <LoadingButton
              variant="contained"
              onClick={handleApplyCoupon}
              disabled={isApplyDisabled || couponActionLoading}
              loading={isApplyCouponLoading || verifyCouponMutation.isLoading}
            >
              {applyButtonLabel}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
    </VBMCardWrapper>
  );
}
